/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import * as React from 'react';
import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { 
  ScanSearch,
  Landmark,
  Search, XCircle,
  TrendingUp, 
  AlertTriangle, 
  ShieldCheck, 
  Info, 
  Loader2,
  ArrowRight,
  FileText,
  Calculator,
  CheckCircle2,
  Layers,
  Filter,
  Zap,
  HelpCircle,
  Settings,
  Shield,
  RotateCcw,
  Activity,
  History,
  AlertCircle,
  LogOut,
  LayoutDashboard,
    Briefcase,
  UserCircle,
  RefreshCw,
  Clock,
  Radio,
  BarChart2,
  Split
} from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { 
  analyzeCompany, 
  getMarketContext, 
  getHistoricalData,
  screenStocks,
  detectPreInflection,
  checkApiKeyStatus,
  type MarketCapCategory,
  type CompanyAnalysis, 
  type MarketContext,
  type PricePoint,
  type OracleCategoryDetail,
  type ScreenerResult,
  type ScreenerOutput,
  type NearMissResult,
  type PreInflectionResult
} from './services/geminiService';
import { cn } from "./lib/utils";
import { toCanvas } from 'html-to-image';
import { jsPDF } from 'jspdf';
import { useDcfModel } from './hooks/useDcfModel';
import { BusinessTab } from './components/Analysis/BusinessTab';
import { MemoTab } from './components/Analysis/MemoTab';
import { ValuationTab } from './components/Analysis/ValuationTab';
import { RiskTab } from './components/Analysis/RiskTab';
import { OverviewTab } from './components/Analysis/OverviewTab';
import { Tooltip } from "./components/Tooltip";
import { TooltipProvider } from "@radix-ui/react-tooltip";
import { auth } from './firebase';
import { sendEmailVerification } from 'firebase/auth';
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { LoginPage } from "./components/Auth/LoginPage";
import { AdminDashboard } from "./components/Admin/AdminDashboard";
import { PreInflectionDetector } from "./components/PreInflectionDetector";
import { PreInflectionScreener } from "./components/PreInflectionScreener";
import { Portfolio } from "./components/Portfolio/Portfolio";
import { SpinoffIntelligence } from "./components/SpinoffIntelligence";
import { LandingPage } from "./components/LandingPage";
import { AccountPage } from "./components/Account/AccountPage";

import { Screener } from './components/Screener/Screener';
import { formatNumber, safeToFixed } from './lib/formatters';
import { SAMPLE_COMPANIES, ValueAssumptions, ScreenerCategoryData, ScreenerResultsMap, PreInflectionScreenResultsMap, PreInflectionScreenCategoryData } from './types/analysis';
import { logVerdict } from './services/verdictLogger';
import { stampOracleAnalysisDate } from './services/holdingsService';

declare global {
  interface Window {
    aistudio?: {
      hasSelectedApiKey: () => Promise<boolean>;
      openSelectKey: () => Promise<void>;
    };
  }
}

import { CapitolDashboard } from './components/CapitolEdge/CapitolDashboard';
import { Logo } from './components/Logo';
import { CapitolUpgradePrompt } from './components/CapitolEdge/CapitolUpgradePrompt';
import { useFeatureFlag } from './hooks/useFeatureFlag';
import { useInactivityTimeout } from './hooks/useInactivityTimeout';
import { useDarkMode } from './hooks/useDarkMode';
import { SessionWarningModal } from './components/SessionWarningModal';
import { subscribeToMemos, getMemo, saveMemo } from './services/memoService';
import type { SavedMemo } from './types/geminiTypes';
import { MemosTab } from './components/Memos/MemosTab';
import { BacktestTab } from './components/Backtest/BacktestTab';
import { AnalysisProgressOverlay } from './components/Analysis/AnalysisProgressOverlay';
import { LegalModal } from './components/Legal/LegalModal';

const INVESTING_QUOTES: { text: string; author: string }[] = [
  { text: "The stock market is a device for transferring money from the impatient to the patient.", author: "Warren Buffett" },
  { text: "Price is what you pay. Value is what you get.", author: "Warren Buffett" },
  { text: "It's far better to buy a wonderful company at a fair price than a fair company at a wonderful price.", author: "Warren Buffett" },
  { text: "Risk comes from not knowing what you're doing.", author: "Warren Buffett" },
  { text: "Be fearful when others are greedy and greedy when others are fearful.", author: "Warren Buffett" },
  { text: "Our favorite holding period is forever.", author: "Warren Buffett" },
  { text: "The most important quality for an investor is temperament, not intellect.", author: "Warren Buffett" },
  { text: "Never invest in a business you cannot understand.", author: "Warren Buffett" },
  { text: "Rule No. 1: Never lose money. Rule No. 2: Never forget Rule No. 1.", author: "Warren Buffett" },
  { text: "If you aren't willing to own a stock for ten years, don't even think about owning it for ten minutes.", author: "Warren Buffett" },
  { text: "It takes 20 years to build a reputation and five minutes to ruin it.", author: "Warren Buffett" },
  { text: "I try to buy stock in businesses that are so wonderful that an idiot can run them. Because sooner or later, one will.", author: "Warren Buffett" },
  { text: "Wide diversification is only required when investors do not understand what they are doing.", author: "Warren Buffett" },
  { text: "You only have to do a very few things right in your life so long as you don't do too many things wrong.", author: "Warren Buffett" },
  { text: "The difference between successful people and really successful people is that really successful people say no to almost everything.", author: "Warren Buffett" },
  { text: "Time is the friend of the wonderful company, the enemy of the mediocre.", author: "Warren Buffett" },
  { text: "Someone is sitting in the shade today because someone planted a tree a long time ago.", author: "Warren Buffett" },
  { text: "Lethargy bordering on sloth remains the cornerstone of our investment style.", author: "Warren Buffett" },
  { text: "Only when the tide goes out do you discover who's been swimming naked.", author: "Warren Buffett" },
  { text: "Games are won by players who focus on the playing field, not by those whose eyes are glued to the scoreboard.", author: "Warren Buffett" },
  { text: "In the business world, the rearview mirror is always clearer than the windshield.", author: "Warren Buffett" },
  { text: "Beware of geeks bearing formulas.", author: "Warren Buffett" },
  { text: "Cash combined with courage in a time of crisis is priceless.", author: "Warren Buffett" },
  { text: "Forecasts may tell you a great deal about the forecaster; they tell you nothing about the future.", author: "Warren Buffett" },
  { text: "Wall Street makes its money on activity. You make your money on inactivity.", author: "Warren Buffett" },
  { text: "The stock market is a no-called-strike game. You don't have to swing at everything — you can wait for your pitch.", author: "Warren Buffett" },
  { text: "Chains of habit are too light to be felt until they are too heavy to be broken.", author: "Warren Buffett" },
  { text: "Predicting rain doesn't count. Building arks does.", author: "Warren Buffett" },
  { text: "The best investment you can make is in yourself.", author: "Warren Buffett" },
  { text: "Opportunities come infrequently. When it rains gold, put out the bucket, not the thimble.", author: "Warren Buffett" },
  { text: "The single most important decision in evaluating a business is pricing power.", author: "Warren Buffett" },
  { text: "Do not save what is left after spending, but spend what is left after saving.", author: "Warren Buffett" },
  { text: "The big money is not in the buying and the selling, but in the waiting.", author: "Charlie Munger" },
  { text: "All intelligent investing is value investing — acquiring more than you are paying for.", author: "Charlie Munger" },
  { text: "A great business at a fair price is superior to a fair business at a great price.", author: "Charlie Munger" },
  { text: "Invert, always invert.", author: "Charlie Munger" },
  { text: "Show me the incentive and I'll show you the outcome.", author: "Charlie Munger" },
  { text: "Go to bed smarter than when you woke up.", author: "Charlie Munger" },
  { text: "A lot of success in life and business comes from knowing what you want to avoid.", author: "Charlie Munger" },
  { text: "Spend each day trying to be a little wiser than you were when you woke up.", author: "Charlie Munger" },
  { text: "It is remarkable how much long-term advantage people like us have gotten by trying to be consistently not stupid.", author: "Charlie Munger" },
  { text: "The wise ones bet heavily when the world offers them that opportunity.", author: "Charlie Munger" },
  { text: "Knowing what you don't know is more useful than being brilliant.", author: "Charlie Munger" },
  { text: "The first rule of compounding: never interrupt it unnecessarily.", author: "Charlie Munger" },
  { text: "People calculate too much and think too little.", author: "Charlie Munger" },
  { text: "We have three baskets for investing: yes, no, and too tough to understand.", author: "Charlie Munger" },
  { text: "I succeeded because I have a long attention span.", author: "Charlie Munger" },
  { text: "You don't have to be brilliant, only a little wiser than the other guys, on average, for a long, long time.", author: "Charlie Munger" },
  { text: "The best thing a human being can do is to help another human being know more.", author: "Charlie Munger" },
  { text: "Take a simple idea and take it seriously.", author: "Charlie Munger" },
  { text: "The iron rule of nature is: you get what you reward for.", author: "Charlie Munger" },
  { text: "It is not greed that drives the world, but envy.", author: "Charlie Munger" },
  { text: "In the short run, the market is a voting machine but in the long run, it is a weighing machine.", author: "Benjamin Graham" },
  { text: "The investor's chief problem — and even his worst enemy — is likely to be himself.", author: "Benjamin Graham" },
  { text: "The intelligent investor is a realist who sells to optimists and buys from pessimists.", author: "Benjamin Graham" },
  { text: "The market is a pendulum that forever swings between unsustainable optimism and unjustified pessimism.", author: "Benjamin Graham" },
  { text: "The individual investor should act consistently as an investor and not as a speculator.", author: "Benjamin Graham" },
  { text: "The essence of investment management is the management of risks, not the management of returns.", author: "Benjamin Graham" },
  { text: "Confronted with the challenge to distill the secret of sound investment into three words, we venture the motto: Margin of Safety.", author: "Benjamin Graham" },
  { text: "An investment operation is one which, upon thorough analysis, promises safety of principal and an adequate return.", author: "Benjamin Graham" },
  { text: "Buy not on optimism, but on arithmetic.", author: "Benjamin Graham" },
  { text: "The intelligent investor is likely to need considerable willpower to keep from following the crowd.", author: "Benjamin Graham" },
  { text: "In the world of securities, courage becomes the supreme virtue after adequate knowledge and a tested judgment are at hand.", author: "Benjamin Graham" },
  { text: "The stock market is filled with individuals who know the price of everything, but the value of nothing.", author: "Philip Fisher" },
  { text: "I don't want a lot of good investments; I want a few outstanding ones.", author: "Philip Fisher" },
  { text: "The greatest investment reward comes to those who find the occasional company that can grow over many years.", author: "Philip Fisher" },
  { text: "Know what you own, and know why you own it.", author: "Peter Lynch" },
  { text: "Behind every stock is a company. Find out what it's doing.", author: "Peter Lynch" },
  { text: "The real key to making money in stocks is not to get scared out of them.", author: "Peter Lynch" },
  { text: "Investing without research is like playing stud poker and never looking at the cards.", author: "Peter Lynch" },
  { text: "You get recessions, you have stock market declines. If you don't understand that's going to happen, you won't do well in the markets.", author: "Peter Lynch" },
  { text: "Far more money has been lost by investors preparing for corrections than has been lost in corrections themselves.", author: "Peter Lynch" },
  { text: "The person that turns over the most rocks wins the game.", author: "Peter Lynch" },
  { text: "In the long run, a portfolio of well-chosen stocks will always outperform a portfolio of bonds or a money-market account.", author: "Peter Lynch" },
  { text: "The four most dangerous words in investing are: 'This time it's different.'", author: "Sir John Templeton" },
  { text: "The time of maximum pessimism is the best time to buy, and the time of maximum optimism is the best time to sell.", author: "Sir John Templeton" },
  { text: "Bull markets are born on pessimism, grow on skepticism, mature on optimism, and die on euphoria.", author: "Sir John Templeton" },
  { text: "If you want to have a better performance than the crowd, you must do things differently from the crowd.", author: "Sir John Templeton" },
  { text: "Value investing is at its core the marriage of a contrarian streak and a calculator.", author: "Seth Klarman" },
  { text: "The single greatest edge an investor can have is a long-term orientation.", author: "Seth Klarman" },
  { text: "Investors should pay attention not to what is happening, but to what could happen.", author: "Seth Klarman" },
  { text: "Value investing requires a great deal of hard work, unusually strict discipline, and a long-term investment horizon.", author: "Seth Klarman" },
  { text: "You can't predict. You can prepare.", author: "Howard Marks" },
  { text: "The riskiest thing in the world is the belief that there's no risk.", author: "Howard Marks" },
  { text: "Being too far ahead of your time is indistinguishable from being wrong.", author: "Howard Marks" },
  { text: "The most dangerous thing is to buy something at the peak of its popularity.", author: "Howard Marks" },
  { text: "Experience is what you got when you didn't get what you wanted.", author: "Howard Marks" },
  { text: "The biggest investing errors come not from factors that are informational or analytical, but from those that are psychological.", author: "Howard Marks" },
  { text: "The secret to investing is to figure out the value of something — and then pay a lot less.", author: "Joel Greenblatt" },
  { text: "Choosing individual stocks without any idea of what you're looking for is like running through a dynamite factory with a burning match.", author: "Joel Greenblatt" },
  { text: "Heads, I win; tails, I don't lose much.", author: "Mohnish Pabrai" },
  { text: "Few bets, big bets, infrequent bets.", author: "Mohnish Pabrai" },
  { text: "Time is your friend; impulse is your enemy.", author: "John Bogle" },
  { text: "The two greatest enemies of the equity fund investor are expenses and emotions.", author: "John Bogle" },
  { text: "It's not whether you're right or wrong that's important, but how much money you make when you're right and how much you lose when you're wrong.", author: "George Soros" },
  { text: "The most contrarian thing of all is not to oppose the crowd but to think for yourself.", author: "Peter Thiel" },
  { text: "In investing, what is comfortable is rarely profitable.", author: "Robert Arnott" },
  { text: "Cash is to a business as oxygen is to an individual: never thought about when it is present, the only thing in mind when it is absent.", author: "Warren Buffett" },
  { text: "Buy when everyone else is selling and hold until everyone else is buying.", author: "J. Paul Getty" },
  { text: "The function of economic forecasting is to make astrology look respectable.", author: "John Kenneth Galbraith" },
];

function MainApp() {
  const { user, profile, isAdmin, isApproved, emailVerified, logout } = useAuth();
  const hasCapitolEdge = useFeatureFlag('capitolEdge');
  const { dark, toggle: toggleDark } = useDarkMode();

  const [verificationSent, setVerificationSent] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showAccount, setShowAccount] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [legalView, setLegalView] = useState<'terms' | 'privacy' | null>(null);
  const [ticker, setTicker] = useState("");
  const [searchSuggestions, setSearchSuggestions] = useState<{symbol: string; name: string; exchangeShortName: string}[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const [analysis, setAnalysis] = useState<CompanyAnalysis | null>(null);
  const [historicalData, setHistoricalData] = useState<PricePoint[]>([]);
  const [chartRange, setChartRange] = useState('1M');
  const [chartTab, setChartTab] = useState<'price' | 'comparison'>('price');
  const [chartLoading, setChartLoading] = useState(false);
  const [marketContext, setMarketContext] = useState<MarketContext | null>(null);
  const [useForwardMetrics, setUseForwardMetrics] = useState(false);
  const [loading, setLoading] = useState(false);
  const [marketLoading, setMarketLoading] = useState(true);
  const [marketError, setMarketError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isExportingScreener, setIsExportingScreener] = useState(false);
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const [loadingMessage, setLoadingMessage] = useState("Calculating Owner Earnings...");
  const [progress, setProgress] = useState(0);
  const [activeTab, setActiveTab] = useState<'analyzer' | 'screener' | 'pre-inflection' | 'portfolio' | 'capitol' | 'memos' | 'backtest' | 'spinoffs'>('analyzer');
  const [activeAnalysisTab, setActiveAnalysisTab] = useState('overview');
  const [savedMemos, setSavedMemos] = useState<SavedMemo[]>([]);
  const [currentMemoAge, setCurrentMemoAge] = useState<number | null>(null);
  const [showRefreshBanner, setShowRefreshBanner] = useState(false);
  const [preInflectionResult, setPreInflectionResult] = useState<PreInflectionResult | null>(null);

  const [loadingSource, setLoadingSource] = useState<'analyzer' | 'memo-overlay' | null>(null);
  const refreshCancelledRef = useRef<boolean>(false);

  const handleCancelRefresh = useCallback(() => {
    refreshCancelledRef.current = true;
    setLoading(false);
    setProgress(0);
    setLoadingMessage("");
    setLoadingSource(null);
  }, []);

  useEffect(() => {
    if (activeTab !== 'memos' && loadingSource === 'memo-overlay') {
      handleCancelRefresh();
    }
  }, [activeTab, loadingSource, handleCancelRefresh]);

  const [preInflectionLoading, setPreInflectionLoading] = useState(false);
  const [screenerLoading, setScreenerLoading] = useState(false);
  const [preInflectionMessage, setPreInflectionMessage] = useState<string>("Scanning for Inflection Signals...");
  const [preInflectionTicker, setPreInflectionTicker] = useState("");
  const [preInflectionMode, setPreInflectionMode] = useState<'detector' | 'screener'>('detector');
  const [selectedCap, setSelectedCap] = useState<MarketCapCategory>('large');
  const [selectedPreInflectionCap, setSelectedPreInflectionCap] = useState<MarketCapCategory>('mid');
  
  const [preInflectionScreenResults, setPreInflectionScreenResults] = useState<PreInflectionScreenResultsMap>(() => {
    const empty: PreInflectionScreenResultsMap = {
      large: { results: [], scannedCount: 0, lastRun: null },
      mid:   { results: [], scannedCount: 0, lastRun: null },
      small: { results: [], scannedCount: 0, lastRun: null }
    };
    try {
      const saved = localStorage.getItem('oracle_preinflection_screen_map');
      if (!saved) return empty;
      const parsed = JSON.parse(saved);
      return {
        large: {
          results: Array.isArray(parsed.large?.results) ? parsed.large.results : [],
          scannedCount: typeof parsed.large?.scannedCount === 'number' ? parsed.large.scannedCount : 0,
          lastRun: parsed.large?.lastRun || null
        },
        mid: {
          results: Array.isArray(parsed.mid?.results) ? parsed.mid.results : [],
          scannedCount: typeof parsed.mid?.scannedCount === 'number' ? parsed.mid.scannedCount : 0,
          lastRun: parsed.mid?.lastRun || null
        },
        small: {
          results: Array.isArray(parsed.small?.results) ? parsed.small.results : [],
          scannedCount: typeof parsed.small?.scannedCount === 'number' ? parsed.small.scannedCount : 0,
          lastRun: parsed.small?.lastRun || null
        }
      };
    } catch {
      return empty;
    }
  });

  useEffect(() => {
    localStorage.setItem('oracle_preinflection_screen_map', JSON.stringify(preInflectionScreenResults));
  }, [preInflectionScreenResults]);

  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(120);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const handleTimeout = async () => {
    setShowWarning(false);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    // MEASUREMENT: confirms idle logouts happen and verifies the run-guard holds.
    // runActiveAtLogout should always be false post-fix; a true value signals a leak.
    fetch('/api/telemetry', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event: 'session_timeout_logout',
        properties: {
          uid: user?.uid || 'anonymous',
          runActiveAtLogout: isRunActive,
          activeTab,
        },
      }),
    }).catch((e) => console.error(e));
    await logout();
  };

  // Any long-running operation that should block auto-logout.
  const isRunActive = loading || preInflectionLoading || screenerLoading;

  const { resetTimer } = useInactivityTimeout({
    timeoutMs: 1800000,
    warningMs: 120000,
    paused: isRunActive,
    onWarning: () => {
      setShowWarning(true);
      setSecondsLeft(120);
      
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = setInterval(() => {
        setSecondsLeft((prev) => {
          if (prev <= 1) {
            if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    },
    onTimeout: handleTimeout,
  });

  const handleStaySignedIn = () => {
    setShowWarning(false);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    setSecondsLeft(120);
    resetTimer();
  };

  // If a run begins while the warning modal is up, dismiss it — the timer is paused.
  useEffect(() => {
    if (isRunActive && showWarning) {
      setShowWarning(false);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
      setSecondsLeft(120);
    }
  }, [isRunActive, showWarning]);

  useEffect(() => {
    return () => {
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };
  }, []);

  useEffect(() => {
    if (user?.uid) {
      const unsubscribe = subscribeToMemos(user.uid, (memos) => {
        setSavedMemos(memos);
      });
      return () => unsubscribe();
    }
  }, [user?.uid]);

  const [screenerResults, setScreenerResults] = useState<ScreenerResultsMap>(() => {
    const empty: ScreenerResultsMap = {
      large: { results: [], nearMisses: [], lastRun: null },
      mid:   { results: [], nearMisses: [], lastRun: null },
      small: { results: [], nearMisses: [], lastRun: null }
    };
    try {
      const saved = localStorage.getItem('oracle_screener_results_map');
      if (!saved) return empty;
      const parsed = JSON.parse(saved);
      return {
        large: {
          results: Array.isArray(parsed.large?.results) ? parsed.large.results :
                   Array.isArray(parsed.large) ? parsed.large : [],
          nearMisses: Array.isArray(parsed.large?.nearMisses) ? parsed.large.nearMisses : [],
          lastRun: parsed.large?.lastRun || null
        },
        mid: {
          results: Array.isArray(parsed.mid?.results) ? parsed.mid.results :
                   Array.isArray(parsed.mid) ? parsed.mid : [],
          nearMisses: Array.isArray(parsed.mid?.nearMisses) ? parsed.mid.nearMisses : [],
          lastRun: parsed.mid?.lastRun || null
        },
        small: {
          results: Array.isArray(parsed.small?.results) ? parsed.small.results :
                   Array.isArray(parsed.small) ? parsed.small : [],
          nearMisses: Array.isArray(parsed.small?.nearMisses) ? parsed.small.nearMisses : [],
          lastRun: parsed.small?.lastRun || null
        }
      };
    } catch (e) {
      console.error("Failed to load screener results from localStorage:", e);
      return empty;
    }
  });
  const [estimatedTime, setEstimatedTime] = useState(45);
  const [currentQuote, setCurrentQuote] = useState(INVESTING_QUOTES[0]);
  const [isForward, setIsForward] = useState(false);
  const [hasApiKey, setHasApiKey] = useState(true);
  const [geminiKeyMissing, setGeminiKeyMissing] = useState(false);
  const [fmpKeyMissing, setFmpKeyMissing] = useState(false);
  const [geminiIsFallback, setGeminiIsFallback] = useState(false);
  const [isProduction, setIsProduction] = useState(false);
  const [geminiSource, setGeminiSource] = useState<string>("unknown");
  const [showKeyHelp, setShowKeyHelp] = useState(false);
  const historyCache = useRef<Record<string, Record<string, PricePoint[]>>>({});
  const memoRef = useRef<HTMLDivElement>(null);
  const screenerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkKeys = async () => {
      // Check for platform-selected key
      if (window.aistudio?.hasSelectedApiKey) {
        const selected = await window.aistudio.hasSelectedApiKey();
        setHasApiKey(selected);
      }
      
      // Check for environment GEMINI_API_KEY
      const status = await checkApiKeyStatus();
      setGeminiKeyMissing(!status.geminiPresent);
      setFmpKeyMissing(!status.fmpPresent);
      setGeminiIsFallback(status.geminiIsFallback);
      setGeminiSource(status.geminiSource);
      setIsProduction(status.isProduction);

      // Fetch market context after keys are ready
      fetchMarket();
    };
    checkKeys();
  }, []);

  useEffect(() => {
    localStorage.setItem('oracle_screener_results_map', JSON.stringify(screenerResults));
  }, [screenerResults]);

  // Rotate investing quotes during analysis loading.
  // Shuffle-bag: every quote is shown once before any repeats; reshuffles on
  // exhaustion (never reached in a normal run) without a back-to-back repeat.
  useEffect(() => {
    if (!loading && !preInflectionLoading) return;

    const shuffle = (arr: typeof INVESTING_QUOTES) => {
      const a = [...arr];
      for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
      }
      return a;
    };

    let bag = shuffle(INVESTING_QUOTES);
    let lastShown: typeof INVESTING_QUOTES[number] | null = null;

    const showNext = () => {
      if (bag.length === 0) {
        bag = shuffle(INVESTING_QUOTES);
        if (bag.length > 1 && bag[0] === lastShown) {
          [bag[0], bag[1]] = [bag[1], bag[0]];
        }
      }
      const next = bag.shift()!;
      lastShown = next;
      setCurrentQuote(next);
    };

    showNext(); // show a fresh random quote immediately when loading begins
    const id = setInterval(showNext, 7000);
    return () => clearInterval(id);
  }, [loading, preInflectionLoading]);

  const handleExportScreenerPDF = async () => {
    const currentResults = screenerResults[selectedCap].results;
    if (currentResults.length === 0) return;
    
    setIsExportingScreener(true);
    try {
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      const contentWidth = pdfWidth - (2 * margin);
      
      let y = margin;

      // Header
      pdf.setFontSize(22);
      pdf.setTextColor(0, 0, 0);
      pdf.text(`THE ORACLE FUNNEL - ${selectedCap.toUpperCase()} CAP`, margin, y);
      y += 10;
      
      pdf.setFontSize(12);
      pdf.setTextColor(100, 100, 100);
      pdf.text(`Investment Screening Report - ${new Date().toLocaleDateString()}`, margin, y);
      y += 15;

      // Disclaimer
      pdf.setFontSize(8);
      pdf.setTextColor(150, 150, 150);
      const disclaimer = "CONFIDENTIAL: This report is generated by Oracle Insight AI. All data is for educational purposes only and does not constitute financial advice.";
      pdf.text(disclaimer, margin, y);
      y += 10;

      // Horizontal Line
      pdf.setDrawColor(200, 200, 200);
      pdf.line(margin, y, pdfWidth - margin, y);
      y += 15;

      // Results
      currentResults.forEach((stock, index) => {
        if (!stock) return;

        // Check if we need a new page
        if (y > pdfHeight - 60) {
          pdf.addPage();
          y = margin;
        }

        // Stock Header
        pdf.setFontSize(16);
        pdf.setTextColor(0, 0, 0);
        pdf.text(`${index + 1}. ${stock.name || 'Unknown'} (${stock.ticker || 'N/A'})`, margin, y);
        
        // Price
        pdf.setFontSize(12);
        pdf.setTextColor(50, 50, 50);
        const priceText = `Current Price: $${safeToFixed(stock.currentPrice, 2)}`;
        const priceWidth = pdf.getTextWidth(priceText);
        pdf.text(priceText, pdfWidth - margin - priceWidth, y);
        y += 8;

        // Sector/Industry
        pdf.setFontSize(10);
        pdf.setTextColor(100, 100, 100);
        pdf.text(`${stock.sector || 'N/A'} | ${stock.industry || 'N/A'}`, margin, y);
        y += 10;

        // Metrics Grid
        pdf.setFontSize(9);
        pdf.setTextColor(0, 0, 0);
        const metrics = [
          `ROIC: ${safeToFixed(stock.metrics?.roic, 1)}%`,
          `ROE: ${safeToFixed(stock.metrics?.roe, 1)}%`,
          `P/E: ${safeToFixed(stock.metrics?.peRatio, 1)}x`,
          `FCF Yield: ${safeToFixed(stock.metrics?.fcfYield, 1)}%`,
          `Rev Growth: ${safeToFixed(stock.metrics?.revenueGrowth5Yr, 1)}%`,
          `D/E: ${safeToFixed(stock.metrics?.debtToEquity, 2)}x`
        ];

        const colWidth = contentWidth / 3;
        metrics.forEach((metric, i) => {
          const col = i % 3;
          const row = Math.floor(i / 3);
          pdf.text(metric, margin + (col * colWidth), y + (row * 6));
        });
        y += 18;

        // Rationale
        pdf.setFontSize(10);
        pdf.setTextColor(50, 50, 50);
        const rationaleLines = pdf.splitTextToSize(`Rationale: ${stock.rationale}`, contentWidth);
        pdf.text(rationaleLines, margin, y);
        y += (rationaleLines.length * 5) + 15;
      });

      // Footer
      const pageCount = pdf.internal.pages.length - 1;
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(150, 150, 150);
        pdf.text(`Page ${i} of ${pageCount}`, pdfWidth / 2, pdfHeight - 10, { align: 'center' });
      }

      pdf.save(`Oracle_Funnel_Report_${new Date().toISOString().split('T')[0]}.pdf`); fetch('/api/telemetry', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ event: 'pdf_exported', properties: { ticker: 'FUNNEL', uid: user?.uid || 'anonymous' } }) }).catch(e => console.error(e));
    } catch (err) {
      console.error("PDF Export Error:", err);
      setError("Failed to generate PDF report. Please try again.");
    } finally {
      setIsExportingScreener(false);
    }
  };

  const handleExportPDF = async () => {
    if (!memoRef.current) return;
    
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 150)); // let react re-render isExporting classes
    try {
      const element = memoRef.current;
      
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      const contentWidth = pdfWidth - (2 * margin);
      
      // 1. Capture all sections in parallel to maximize performance
      const sections = Array.from(element.querySelectorAll('.pdf-section')).filter((el) => {
        const hElement = el as HTMLElement;
        return hElement.offsetHeight > 0 && hElement.offsetWidth > 0 && !hElement.parentElement?.closest('.pdf-section');
      });
      
      // Using toCanvas is faster as it avoids the JPEG encoding step during capture
      const capturedSections = await Promise.all(sections.map(async (section) => {
        const s = section as HTMLElement;
        const keepWithNext = s.classList.contains('pdf-keep-with-next');
        const sectionStyle = window.getComputedStyle(s);
        const sectionBg = sectionStyle.backgroundColor;
        
        let canvas;
        const filter = (node: Node) => {
          // Skip external stylesheets that cause CORS errors when accessed by html-to-image
          if (node instanceof HTMLLinkElement && node.rel === 'stylesheet' && node.href.includes('fonts.googleapis.com')) {
            return false;
          }
          return true;
        };

        try {
          canvas = await toCanvas(s, {
            pixelRatio: 2,
            backgroundColor: sectionBg !== 'rgba(0, 0, 0, 0)' && sectionBg !== 'transparent' ? sectionBg : '#FFFFFF',
            skipFonts: false,
            cacheBust: false,
            filter,
          });
        } catch (err) {
          console.warn("toCanvas failed with fonts, retrying without fonts:", err);
          try {
            canvas = await toCanvas(s, {
              pixelRatio: 2,
              backgroundColor: sectionBg !== 'rgba(0, 0, 0, 0)' && sectionBg !== 'transparent' ? sectionBg : '#FFFFFF',
              skipFonts: true,
              cacheBust: false,
              filter,
            });
          } catch (fallbackErr) {
            console.error("toCanvas completely failed for section:", fallbackErr);
            // If it completely fails, we return a blank canvas or skip it
            canvas = document.createElement('canvas');
            canvas.width = 1;
            canvas.height = 1;
          }
        }

        return { canvas, keepWithNext };
      }));

      let currentPdfY = margin;

      for (let i = 0; i < capturedSections.length; i++) {
        const { canvas, keepWithNext } = capturedSections[i];
        
        // Calculate dimensions in mm
        const imgWidthPx = canvas.width;
        const imgHeightPx = canvas.height;
        const imgHeight = (imgHeightPx * contentWidth) / imgWidthPx;

        let needsNewPage = currentPdfY + imgHeight > pdfHeight - margin;

        if (!needsNewPage && keepWithNext && i + 1 < capturedSections.length) {
          const nextCanvas = capturedSections[i + 1].canvas;
          const nextImgHeight = (nextCanvas.height * contentWidth) / nextCanvas.width;
          if (currentPdfY + imgHeight + 2 + nextImgHeight > pdfHeight - margin) {
            needsNewPage = true;
          }
        }

        // If this section would overflow the current page, start a new page
        if (needsNewPage && currentPdfY > margin) {
          pdf.addPage();
          currentPdfY = margin;
        }

        if (currentPdfY + imgHeight <= pdfHeight - margin) {
          // Fits on current page - use canvas directly
          pdf.addImage(canvas, 'JPEG', margin, currentPdfY, contentWidth, imgHeight, undefined, 'FAST');
          currentPdfY += imgHeight + 2;
        } else {
          // Needs slicing because it's taller than a full page
          let remainingHeight = imgHeight;
          let sourceY = 0; // in mm
          const scale = imgWidthPx / contentWidth; // pixels per mm

          while (remainingHeight > 0) {
            if (currentPdfY >= pdfHeight - margin) {
              pdf.addPage();
              currentPdfY = margin;
            }

            const availableHeight = pdfHeight - margin - currentPdfY;
            const sliceHeight = Math.min(remainingHeight, availableHeight);

            const sliceCanvas = document.createElement('canvas');
            sliceCanvas.width = imgWidthPx;
            sliceCanvas.height = sliceHeight * scale;
            
            const ctx = sliceCanvas.getContext('2d');
            if (ctx) {
              ctx.drawImage(
                canvas, 
                0, sourceY * scale, imgWidthPx, sliceHeight * scale,
                0, 0, sliceCanvas.width, sliceCanvas.height
              );
              
              pdf.addImage(sliceCanvas, 'JPEG', margin, currentPdfY, contentWidth, sliceHeight, undefined, 'FAST');
            }
            
            currentPdfY += sliceHeight + (remainingHeight > availableHeight ? 0 : 2);
            sourceY += sliceHeight;
            remainingHeight -= sliceHeight;
          }
        }
      }

      // Add professional institutional footer to each page
      const pageCount = pdf.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(7);
        pdf.setTextColor(160, 160, 160);
        
        // Left side: Confidentiality
        pdf.text('CONFIDENTIAL - FOR INSTITUTIONAL USE ONLY', margin, pdfHeight - 6);
        
        // Center: Ticker/Date
        const dateStr = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
        const centerText = `${analysis?.ticker || ''} Analysis | ${dateStr}`;
        const centerTextWidth = pdf.getStringUnitWidth(centerText) * 7 / pdf.internal.scaleFactor;
        pdf.text(centerText, (pdfWidth - centerTextWidth) / 2, pdfHeight - 6);
        
        // Right side: Page X of Y
        const pageText = `Page ${i} of ${pageCount}`;
        const pageTextWidth = pdf.getStringUnitWidth(pageText) * 7 / pdf.internal.scaleFactor;
        pdf.text(pageText, pdfWidth - margin - pageTextWidth, pdfHeight - 6);
        
        // Subtle line above footer
        pdf.setDrawColor(230, 230, 230);
        pdf.setLineWidth(0.1);
        pdf.line(margin, pdfHeight - 9, pdfWidth - margin, pdfHeight - 9);
      }

      pdf.save(`${analysis?.ticker || 'Investment'}_Memo.pdf`); fetch('/api/telemetry', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ event: 'pdf_exported', properties: { ticker: analysis?.ticker || 'UNKNOWN', uid: user?.uid || 'anonymous' } }) }).catch(e => console.error(e));
    } catch (err) {
      console.error("PDF Export failed:", err);
      setError("Failed to export PDF. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const fetchMarket = async () => {
    setMarketLoading(true);
    setMarketError(null);
    try {
      const data = await getMarketContext();
      setMarketContext(data);
    } catch (err: any) {
      console.error("Failed to fetch market context:", err);
      const errorStr = typeof err === 'string' ? err : JSON.stringify(err);
      const errorMsg = err?.message || err?.error?.message || "";
      const combinedMsg = `${errorStr} ${errorMsg}`.toLowerCase();
      const statusCode = err?.status || err?.error?.code || err?.code || 0;

      if (combinedMsg.includes("429") || statusCode === 429 || combinedMsg.includes("resource_exhausted") || combinedMsg.includes("quota") || combinedMsg.includes("rate exceeded")) {
        setMarketError("AI rate limit reached. Too many requests were sent in a short window. Please wait a moment and try again.");
      } else if (combinedMsg.includes("503") || statusCode === 503 || combinedMsg.includes("unavailable") || combinedMsg.includes("high demand") || combinedMsg.includes("overloaded")) {
        setMarketError("Market data service is currently under high demand. Please try again later.");
      } else {
        setMarketError("Failed to load market context.");
      }
    } finally {
      setMarketLoading(false);
    }
  };

  const [assumptions, setAssumptions] = useState<ValueAssumptions>({
    revenueGrowth: 8,
    marginExpansion: 0,
    discountRate: 9,
    terminalGrowth: 3,
    marginOfSafety: 15
  });
  const [scorecard, setScorecard] = useState({
    moat: "Narrow",
    management: "Good",
    understandable: "Moderate",
    longTerm: "Medium",
    marginOfSafety: "Insufficient",
    stability: "Moderate"
  });
  const [initialAssumptions, setInitialAssumptions] = useState<ValueAssumptions | null>(null);

  const handleDetectPreInflection = async (targetTicker: string) => {
    setPreInflectionLoading(true);
    setPreInflectionResult(null);
    setError(null);
    setPreInflectionMessage("Fetching quarterly financials and recent news...");

    try {
      const now = new Date();
      const currentDateTime = `${now.toUTCString()} (User Local Time: ${now.toLocaleString()})`;

      let result: PreInflectionResult | null = null;

      await new Promise<void>(async (resolve, reject) => {
        const token = await auth.currentUser?.getIdToken();
        const url = `/api/pre-inflection-stream?ticker=${encodeURIComponent(targetTicker)}&currentDateTime=${encodeURIComponent(currentDateTime)}${token ? `&token=${encodeURIComponent(token)}` : ''}`;
        const es = new EventSource(url);

        es.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);

            if (data.stage === "error") {
              es.close();
              reject(new Error(data.error || "Unknown server error"));
              return;
            }

            if (data.stage === "done") {
              es.close();
              result = data.result;
              resolve();
              return;
            }

            if (data.message) setPreInflectionMessage(data.message);

          } catch (e) {
            // Ignore malformed events
          }
        };

        es.onerror = () => {
          es.close();
          reject(new Error("Connection to analysis server was lost. Please try again."));
        };
      });

      if (result) setPreInflectionResult(result);

    } catch (err: any) {
      console.error("Pre-Inflection detection error:", err);
      const errorMsg = err?.message || String(err);
      if (errorMsg.includes("NOT_FOUND")) {
        setError(`The stock symbol '${targetTicker}' is not listed on any of the markets that Oracle Insights analyzes. Please verify the symbol and try again.`);
      } else {
        setError("Failed to run pre-inflection analysis. Please try again.");
      }
    } finally {
      setPreInflectionLoading(false);
    }
  };

  // Stable ref so suggestion select can call handleSearch without stale closure
  const handleSearchRef = useRef<(t: string) => void>(() => {});

  const fetchSearchSuggestions = useCallback(async (query: string) => {
    if (!query || query.length < 2) {
      setSearchSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    setSearchLoading(true);
    try {
      // Run symbol search and name search in parallel using the correct stable endpoints.
      // v3/search is a legacy endpoint (403 for new keys). stable/search is not a valid path (404).
      // The correct endpoints are stable/search-symbol and stable/search-name.
      const encodedQuery = encodeURIComponent(query);
      const [symbolRes, nameRes] = await Promise.allSettled([
        fetch(`/api/fmp/stable/search-symbol?query=${encodedQuery}&limit=8`),
        fetch(`/api/fmp/stable/search-name?query=${encodedQuery}&limit=8`)
      ]);

      const seen = new Set<string>();
      const merged: { symbol: string; name: string; exchangeShortName: string }[] = [];

      for (const result of [symbolRes, nameRes]) {
        if (result.status === 'fulfilled' && result.value.ok) {
          const data = await result.value.json();
          if (Array.isArray(data)) {
            for (const item of data) {
              if (item.symbol && !seen.has(item.symbol)) {
                seen.add(item.symbol);
                merged.push({
                  symbol: item.symbol,
                  name: item.name || item.companyName || '',
                  exchangeShortName: item.exchangeShortName || item.exchange || ''
                });
              }
            }
          }
        }
      }

      if (merged.length > 0) {
        setSearchSuggestions(merged.slice(0, 8));
        setShowSuggestions(true);
        setActiveSuggestionIndex(-1);
      } else {
        setSearchSuggestions([]);
        setShowSuggestions(false);
      }
    } catch {
      setSearchSuggestions([]);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  const handleTickerInput = useCallback((raw: string) => {
    // Pure ticker-like strings (e.g. AAPL, BRK-B): uppercase and apply dot-to-dash
    // Name queries (spaces or mixed case): pass through unchanged so FMP can search them
    const isTickerMode = /^[A-Z0-9.\-]{1,10}$/.test(raw) && !raw.includes(' ');
    if (isTickerMode) {
      setTicker(raw.toUpperCase().replace(/\./g, '-'));
    } else {
      setTicker(raw);
    }
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      fetchSearchSuggestions(raw);
    }, 220);
  }, [fetchSearchSuggestions]);

  const handleSuggestionSelect = useCallback((symbol: string) => {
    const normalized = symbol.toUpperCase().replace(/\./g, '-');
    setTicker(normalized);
    setShowSuggestions(false);
    setSearchSuggestions([]);
    setActiveSuggestionIndex(-1);
    // setTimeout ensures state flushes before analysis fires
    setTimeout(() => handleSearchRef.current(normalized), 0);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = async (
    targetTicker: string, 
    forceRefresh = false, 
    source: 'analyzer' | 'memo-overlay' = 'analyzer',
    startOptions?: { source?: 'funnel' | 'preInflection'; logId?: string | null }
  ) => {
    refreshCancelledRef.current = false;
    setLoadingSource(source);

    if (source === 'analyzer') {
      handleSearchRef.current = handleSearch;
      setActiveTab('analyzer');
      setShowRefreshBanner(false);
    }
    
    setTicker(targetTicker);
    setError(null);
    setCurrentMemoAge(null);

    if (!forceRefresh && user?.uid) {
      const existingMemo = await getMemo(user.uid, targetTicker);
      if (existingMemo) {
        setAnalysis(existingMemo.analysisResult);
        
        // Calculate age
        const runAtDate = existingMemo.runAt?.toDate ? existingMemo.runAt.toDate() : new Date();
        const ageDays = Math.floor((Date.now() - runAtDate.getTime()) / (1000 * 60 * 60 * 24));
        setCurrentMemoAge(ageDays);
        setShowRefreshBanner(true);
        
        // Setup initial assumptions from the memo's result (same logic as below)
        const result = existingMemo.analysisResult;
        if (result.fmpDcf?.customDcfData) {
          const custom = result.fmpDcf.customDcfData;
          const initial: ValueAssumptions = {
            revenueGrowth: Math.round(custom.revenuePercentage || (result.suggestedAssumptions?.revenueGrowth ? (result.suggestedAssumptions.revenueGrowth > 1 ? result.suggestedAssumptions.revenueGrowth : result.suggestedAssumptions.revenueGrowth * 100) : 10)),
            marginExpansion: result.suggestedAssumptions?.marginExpansion || 0,
            discountRate: Math.round(custom.wacc || (result.suggestedAssumptions?.discountRate ? (result.suggestedAssumptions.discountRate > 1 ? result.suggestedAssumptions.discountRate : result.suggestedAssumptions.discountRate * 100) : 10)),
            terminalGrowth: Math.round(custom.longTermGrowthRate || (result.suggestedAssumptions?.terminalGrowth ? (result.suggestedAssumptions.terminalGrowth > 1 ? result.suggestedAssumptions.terminalGrowth : result.suggestedAssumptions.terminalGrowth * 100) : 3)),
            marginOfSafety: 25
          };
          setAssumptions(initial);
          setInitialAssumptions(initial);
        } else if (result.suggestedAssumptions) {
          const initial: ValueAssumptions = {
            revenueGrowth: Math.round(result.suggestedAssumptions.revenueGrowth > 1 ? result.suggestedAssumptions.revenueGrowth : result.suggestedAssumptions.revenueGrowth * 100),
            marginExpansion: result.suggestedAssumptions.marginExpansion || 0,
            discountRate: Math.round(result.suggestedAssumptions.discountRate > 1 ? result.suggestedAssumptions.discountRate : result.suggestedAssumptions.discountRate * 100),
            terminalGrowth: Math.round(result.suggestedAssumptions.terminalGrowth > 1 ? result.suggestedAssumptions.terminalGrowth : result.suggestedAssumptions.terminalGrowth * 100),
            marginOfSafety: 25
          };
          setAssumptions(initial);
          setInitialAssumptions(initial);
        }
        
        if (result.scorecard) {
          setScorecard({
            ...result.scorecard,
            stability: result.scorecard.stability || "Moderate"
          });
        }
        
        return; // Skip normal analysis
      }
    }

    setLoading(true);
    setLoadingMessage("Initiating Deep Analysis...");
    setProgress(0);
    setEstimatedTime(45); // Default estimate
    
    try {
      const now = new Date();
      const currentDateTime = `${now.toUTCString()} (User Local Time: ${now.toLocaleString()})`;
      
      let result: any = null;
      
      // Step 1: Start the analysis job and get a jobId
      const token = await auth.currentUser?.getIdToken();
      const startRes = await fetch('/api/start-analysis', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ 
          ticker: targetTicker, 
          currentDateTime,
          ...(startOptions?.source ? { source: startOptions.source } : {}),
          ...(startOptions?.logId ? { logId: startOptions.logId } : {})
        })
      });

      if (!startRes.ok) {
        const errData = await startRes.json().catch(() => ({}));
        throw new Error(errData.error || `Failed to start analysis (${startRes.status})`);
      }

      const { jobId } = await startRes.json();
      if (refreshCancelledRef.current) return;
      setCurrentJobId(jobId);

      // Step 2: Poll for progress and result
      // Polling survives app backgrounding, screen lock, and app switching.
      // The job runs server-side regardless of client connection state.
      await new Promise<void>((resolve, reject) => {
        let settled = false;

        const poll = async () => {
          if (settled) return;
          if (refreshCancelledRef.current) {
            settled = true;
            resolve();
            return;
          }

          try {
            const token = await auth.currentUser?.getIdToken();
            const statusRes = await fetch(`/api/job-status/${jobId}`, {
              headers: {
                ...(token ? { 'Authorization': `Bearer ${token}` } : {})
              }
            });

            if (statusRes.status === 404) {
              // Job not found — server restart or scaling event lost the job
              if (!settled) {
                settled = true;
                reject(new Error("Analysis session expired. Please run the analysis again."));
              }
              return;
            }

            if (!statusRes.ok) {
              // Transient network error — keep polling
              scheduleNextPoll();
              return;
            }

            const data = await statusRes.json();

            if (data.status === 'error') {
              if (!settled) {
                settled = true;
                reject(new Error(data.error || 'Analysis failed'));
              }
              return;
            }

            if (data.status === 'done') {
              if (!settled) {
                settled = true;
                if (!refreshCancelledRef.current) {
                  setProgress(100);
                  result = data.result;
                }
                resolve();
              }
              return;
            }

            // Still running — update UI and schedule next poll
            if (!refreshCancelledRef.current) {
              if (typeof data.pct === 'number') setProgress(data.pct);
              if (data.message) setLoadingMessage(data.message);
            }
            scheduleNextPoll();

          } catch {
            // Network error (user is backgrounded, no connection momentarily)
            // Keep polling — this is exactly what polling is designed to handle
            scheduleNextPoll();
          }
        };

        const scheduleNextPoll = () => {
          if (!settled && !refreshCancelledRef.current) setTimeout(poll, 4000);
          else if (refreshCancelledRef.current && !settled) {
            settled = true;
            resolve();
          }
        };

        // Start first poll after a short delay to give the server time to
        // begin the analysis before we check status
        setTimeout(poll, 2000);
      });

      if (refreshCancelledRef.current) return;

      setCurrentJobId(null);
      setLoadingSource(null);
      
      if (!result) return;
      setAnalysis(result);

      // Log verdict to Firestore (fire-and-forget — never blocks UI)
      if (user?.uid) {
        logVerdict(user.uid, result);
        saveMemo(user.uid, targetTicker, result); // Save to memos
        stampOracleAnalysisDate(user.uid, targetTicker).catch(() => {}); // fire-and-forget
      }
      
      if (result.fmpDcf?.customDcfData) {
        const custom = result.fmpDcf.customDcfData;
        const initial: ValueAssumptions = {
          revenueGrowth: Math.round(custom.revenuePercentage || (result.suggestedAssumptions?.revenueGrowth ? (result.suggestedAssumptions.revenueGrowth > 1 ? result.suggestedAssumptions.revenueGrowth : result.suggestedAssumptions.revenueGrowth * 100) : 10)),
          marginExpansion: result.suggestedAssumptions?.marginExpansion || 0,
          discountRate: Math.round(custom.wacc || (result.suggestedAssumptions?.discountRate ? (result.suggestedAssumptions.discountRate > 1 ? result.suggestedAssumptions.discountRate : result.suggestedAssumptions.discountRate * 100) : 10)),
          terminalGrowth: Math.round(custom.longTermGrowthRate || (result.suggestedAssumptions?.terminalGrowth ? (result.suggestedAssumptions.terminalGrowth > 1 ? result.suggestedAssumptions.terminalGrowth : result.suggestedAssumptions.terminalGrowth * 100) : 3)),
          marginOfSafety: 25
        };
        setAssumptions(initial);
        setInitialAssumptions(initial);
      } else if (result.suggestedAssumptions) {
        const initial: ValueAssumptions = {
          revenueGrowth: Math.round(result.suggestedAssumptions.revenueGrowth > 1 ? result.suggestedAssumptions.revenueGrowth : result.suggestedAssumptions.revenueGrowth * 100),
          marginExpansion: result.suggestedAssumptions.marginExpansion || 0,
          discountRate: Math.round(result.suggestedAssumptions.discountRate > 1 ? result.suggestedAssumptions.discountRate : result.suggestedAssumptions.discountRate * 100),
          terminalGrowth: Math.round(result.suggestedAssumptions.terminalGrowth > 1 ? result.suggestedAssumptions.terminalGrowth : result.suggestedAssumptions.terminalGrowth * 100),
          marginOfSafety: 25
        };
        setAssumptions(initial);
        setInitialAssumptions(initial);
      }

      if (result.scorecard) {
        setScorecard({
          ...result.scorecard,
          stability: result.scorecard.stability || "Moderate"
        });
      }
      
    } catch (err: any) {
      if (refreshCancelledRef.current) return;
      setCurrentJobId(null);
      setLoadingSource(null);
      const errorStr = typeof err === 'string' ? err : (err instanceof Error ? err.message : JSON.stringify(err));
      if (!errorStr.includes("NOT_FOUND")) {
        console.error("Search error:", err);
      }
      const errorMsg = err?.message || err?.error?.message || "";
      const combinedMsg = `${errorStr} ${errorMsg}`.toLowerCase();
      const statusCode = err?.status || err?.error?.code || err?.code || 0;

      if (combinedMsg.includes("429") || statusCode === 429 || statusCode === "429" || combinedMsg.includes("resource_exhausted") || combinedMsg.includes("quota") || combinedMsg.includes("rate exceeded")) {
        setError("AI rate limit reached. Too many requests were sent in a short window. Please wait a moment and try again.");
      } else if (combinedMsg.includes("503") || statusCode === 503 || statusCode === "503" || combinedMsg.includes("unavailable") || combinedMsg.includes("high demand") || combinedMsg.includes("overloaded") || (statusCode === 500 || statusCode === "500" || combinedMsg.includes("500") || combinedMsg.includes("internal server error"))) {
        setError("The AI service is currently experiencing high demand or a transient network issue. We've tried retrying several times, but it's still busy. Please try again in a few moments.");
      } else if (combinedMsg.includes("forbidden") || combinedMsg.includes("403") || statusCode === 403 || statusCode === "403") {
        if (combinedMsg.includes("fmp") || combinedMsg.includes("financialmodelingprep")) {
          setError(`FMP Access Forbidden: Your Financial Modeling Prep API key may be invalid, reached its limit, or doesn't have access to ${targetTicker}. Please verify your FMP_API_KEY in the Settings menu.`);
        } else if (combinedMsg.includes("generativelanguage") || combinedMsg.includes("gemini")) {
          setError(`Gemini AI Access Forbidden: The AI service rejected the request. This usually happens when the API key is restricted or the Generative Language API is not enabled. Please ensure you have a valid GEMINI_API_KEY in the Settings menu.`);
        } else {
          setError(`Access Forbidden (403): The request was rejected by the service. Details: ${errorMsg || errorStr}`);
        }
      } else if (combinedMsg.includes("not_found")) {
        setError(`The stock symbol '${targetTicker}' is not listed on any of the markets that Oracle Insights analyzes. Please verify the symbol and try again.`);
      } else if (combinedMsg.includes("safety") || combinedMsg.includes("blocked")) {
        setError("The AI analysis was blocked by safety filters. This can happen with certain sensitive topics or controversial companies.");
      } else if (combinedMsg.includes("api key") || combinedMsg.includes("unauthorized") || combinedMsg.includes("invalid key")) {
        setError(`API Key Error: One of your keys (Gemini or FMP) is invalid or missing. Gemini Status: ${geminiKeyMissing ? "MISSING" : "Present"}, FMP Status: ${fmpKeyMissing ? "MISSING" : "Present"}. Details: ${errorMsg || errorStr}. Please check the Settings menu.`);
      } else if (combinedMsg.includes("json") || combinedMsg.includes("empty response body")) {
        setError(`The AI returned an invalid or incomplete response structure. This occasionally happens with complex models. Please try again.`);
      } else {
        setError(`We couldn't complete the analysis for ${targetTicker}. This was an internal processing issue on our end — not a problem with your account or keys. Please try again in a moment.`);
      }
    } finally {
      if (!refreshCancelledRef.current) {
        setCurrentJobId(null);
        setLoading(false);
      }
    }
  };

  const resetAssumptions = () => {
    if (!initialAssumptions) return;
    setAssumptions(initialAssumptions);
  };

  const comparisonData = useMemo(() => {
    if (historicalData.length === 0) return [];
    
    // Find first point where both have data if possible, otherwise use first valid for each
    const firstValidStock = historicalData.find(p => p.price > 0);
    const firstPrice = firstValidStock ? firstValidStock.price : 0;
    
    const firstValidSpy = historicalData.find(p => p.spyPrice && p.spyPrice > 0);
    const firstSpyPrice = firstValidSpy ? firstValidSpy.spyPrice : 0;

    return historicalData.map(p => ({
      ...p,
      stockGrowth: (firstPrice > 0 && p.price) ? ((p.price - firstPrice) / firstPrice) * 100 : 0,
      spyGrowth: (firstSpyPrice > 0 && p.spyPrice) ? ((p.spyPrice - firstSpyPrice) / firstSpyPrice) * 100 : 0
    }));
  }, [historicalData, ticker]);

  const fetchHistoricalData = async (ticker: string, range: string) => {
    // Check cache first to avoid redundant API calls
    if (historyCache.current[ticker]?.[range]) {
      setHistoricalData(historyCache.current[ticker][range]);
      return;
    }

    setChartLoading(true);
    try {
      const data = await getHistoricalData(ticker, range);
      
      // Store in cache
      if (!historyCache.current[ticker]) {
        historyCache.current[ticker] = {};
      }
      historyCache.current[ticker][range] = data;
      
      setHistoricalData(data);
      
      // [F2 HEADER-SNAPSHOT-BIND] (Tuning v2.93): a "sync" block here previously
      // OVERWROTE analysis.currentPrice with the chart's last data point for short
      // ranges (1D/5D/1M), rebinding the memo header, the premium card, and every
      // other analysis.currentPrice consumer to a live intraday price — while all
      // engine math, the post-Oracle locks (PRICE-PROSE-LOCK, IV-LOCK), premiums,
      // and IRRs were computed on the job-time quote snapshot. That mutation was
      // the root of the two-price memo family (NBIX 179.27 header vs 179.01
      // analysis, SU 58.88/58.79, OII 40.81/40.85, ADUS 105.60/106.32, MFP,
      // BRK-B triple-source). The analysis object is immutable post-pipeline;
      // the chart renders its own latest point independently.
    } catch (err) {
      console.error("Failed to fetch historical data:", err);
    } finally {
      setChartLoading(false);
    }
  };

  useEffect(() => {
    if (analysis?.ticker) {
      fetchHistoricalData(analysis.ticker, chartRange);
    }
  }, [analysis?.ticker, chartRange]);

  const formatCurrencyValue = (millions: number | undefined | null) => {
    if (millions === undefined || millions === null || isNaN(millions)) return 'N/A';
    if (Math.abs(millions) >= 1000) {
      return `$${safeToFixed(millions / 1000, 1)}B`;
    }
    return `$${safeToFixed(millions, 0)}M`;
  };

  const dcfModel = useDcfModel(analysis, marketContext, assumptions);

  // Scenarios calculation (Removed decoupled logic)


  return (
    <TooltipProvider delayDuration={200}>
      <div className="min-h-screen bg-paper text-ink font-sans selection:bg-oxblood selection:text-paper pb-36 md:pb-20">
      {/* Header */}
      <header className="border-b border-ink-200 bg-paper/85 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between gap-8">
          <button 
            onClick={() => {
              setAnalysis(null);
              setTicker("");
              setShowAdmin(false);
              setError(null);
            }}
            className="flex items-center gap-3 shrink-0 hover:opacity-80 transition-opacity active:scale-95"
          >
            <Logo size={34} variant="lockup" />
          </button>
          
          <div className="flex-1" />

          <div className="hidden md:flex items-center gap-4 border-l border-ink-200 pl-6 ml-2">
            <button 
              onClick={() => setActiveTab('analyzer')}
              className={cn(
                "text-[10px] font-medium uppercase tracking-widest transition-colors px-1 py-2 flex items-center gap-2 border-b-2",
                activeTab === 'analyzer' ? "border-oxblood text-oxblood" : "border-transparent text-ink-600 hover:text-ink"
              )}
            >
              <ScanSearch className="w-3.5 h-3.5" />
              <span>Analyzer</span>
            </button>
            <button 
              onClick={() => setActiveTab('screener')}
              className={cn(
                "text-[10px] font-medium uppercase tracking-widest transition-colors px-1 py-2 flex items-center gap-2 border-b-2",
                activeTab === 'screener' ? "border-oxblood text-oxblood" : "border-transparent text-ink-600 hover:text-ink"
              )}
            >
              <Filter className="w-3.5 h-3.5" />
              <span>The Funnel</span>
            </button>
            <button 
              onClick={() => setActiveTab('pre-inflection')}
              className={cn(
                "text-[10px] font-medium uppercase tracking-widest transition-colors px-1 py-2 flex items-center gap-2 border-b-2",
                activeTab === 'pre-inflection' ? "border-oxblood text-oxblood" : "border-transparent text-ink-600 hover:text-ink"
              )}
            >
              <TrendingUp className="w-3.5 h-3.5" />
              <span>Pre-Inflection</span>
            </button>
            <button 
              onClick={() => setActiveTab('spinoffs')}
              className={cn(
                "text-[10px] font-medium uppercase tracking-widest transition-colors px-1 py-2 flex items-center gap-2 border-b-2",
                activeTab === 'spinoffs' ? "border-oxblood text-oxblood" : "border-transparent text-ink-600 hover:text-ink"
              )}
            >
              <Split className="w-3.5 h-3.5" />
              <span>New Listings</span>
            </button>
            <button 
              onClick={() => setActiveTab('portfolio')}
              className={cn(
                "text-[10px] font-medium uppercase tracking-widest transition-colors px-1 py-2 flex items-center gap-2 border-b-2",
                activeTab === 'portfolio' ? "border-oxblood text-oxblood" : "border-transparent text-ink-600 hover:text-ink"
              )}
            >
              <Briefcase className="w-3.5 h-3.5" />
              <span>Portfolio</span>
            </button>
            <button 
              onClick={() => setActiveTab('memos')}
              className={cn(
                "text-[10px] font-medium uppercase tracking-widest transition-colors px-1 py-2 flex items-center gap-2 border-b-2 relative",
                activeTab === 'memos' ? "border-oxblood text-oxblood" : "border-transparent text-ink-600 hover:text-ink"
              )}
            >
              <FileText className="w-3.5 h-3.5" />
              <span>My Memos</span>
              {savedMemos.length > 0 && (
                <span className="absolute -top-1.5 -right-1.5 bg-oxblood text-paper text-[8px] font-medium px-1.5 py-0.5 rounded-sm min-w-[16px] text-center">
                  {savedMemos.length}
                </span>
              )}
            </button>
            {hasCapitolEdge && (
              <button 
                onClick={() => setActiveTab('capitol')}
                className={cn(
                  "text-[10px] font-medium uppercase tracking-widest transition-colors px-1 py-2 flex items-center gap-2 border-b-2",
                  activeTab === 'capitol' ? "border-oxblood text-oxblood" : "border-transparent text-ink-600 hover:text-ink"
                )}
              >
                <Landmark className="w-3.5 h-3.5" />
                <span>Capitol</span>
              </button>
            )}
            <button 
              onClick={() => setActiveTab('backtest')}
              className={cn(
                "text-[10px] font-medium uppercase tracking-widest transition-colors px-1 py-2 flex items-center gap-2 border-b-2",
                activeTab === 'backtest' ? "border-oxblood text-oxblood" : "border-transparent text-ink-600 hover:text-ink"
              )}
            >
              <BarChart2 className="w-3.5 h-3.5" />
              <span>Backtest</span>
            </button>
            {/* TODO: Replace hide with upgrade prompt when billing live */}
          </div>

          <div className="hidden md:flex items-center gap-6">
            {isAdmin && (
              <button 
                onClick={() => setShowAdmin(!showAdmin)}
                className={cn(
                  "text-[10px] font-medium uppercase tracking-widest transition-colors flex items-center gap-2 px-3 py-1.5 rounded-md",
                  showAdmin ? "bg-oxblood text-paper" : "text-ink-600 hover:text-ink"
                )}
              >
                <LayoutDashboard className="w-3.5 h-3.5" /> 
                <span>{showAdmin ? "Back to App" : "Admin"}</span>
              </button>
            )}
            <button 
              onClick={logout}
              className="text-[10px] font-medium uppercase tracking-widest text-ink-600 hover:text-ink transition-colors flex items-center gap-2 px-3 py-1.5 rounded-md"
            >
              <LogOut className="w-3.5 h-3.5" /> 
              <span>Sign Out</span>
            </button>
            <button 
              onClick={() => {
                setShowAccount(!showAccount);
                setShowAdmin(false);
              }}
              className={cn(
                "text-[10px] font-medium uppercase tracking-widest transition-colors flex items-center gap-2 px-3 py-1.5 rounded-md",
                showAccount ? "bg-oxblood text-paper" : "text-ink-600 hover:text-ink"
              )}
            >
              <Settings className="w-3.5 h-3.5" /> 
              <span>Settings</span>
            </button>
          </div>
        </div>
      </header>



      {/* API Key Warning Banner */}
      <AnimatePresence>
        {geminiKeyMissing && (
          <motion.div 
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="bg-warning-50 border-b border-warning-100 overflow-hidden"
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 text-warning-700">
                <AlertTriangle className="w-5 h-5 shrink-0" />
                <p className="text-sm font-medium">
                  <span className="font-bold">Gemini AI is not configured.</span> Deep qualitative analysis and Oracle Math synthesis are disabled. 
                  Please add your key in the <span className="font-bold">Settings</span> menu to enable full functionality.
                </p>
              </div>
              <button 
                onClick={() => setShowKeyHelp(true)}
                className="bg-warning-100 hover:bg-warning-50 text-warning-700 text-[10px] font-bold uppercase tracking-wider px-4 py-2 rounded-lg transition-colors shrink-0"
              >
                Configure Key
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {showAdmin ? (
        <AdminDashboard />
      ) : showAccount ? (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
          <button 
            onClick={() => setShowAccount(false)}
            className="mb-8 flex flex-row items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-ink-500 hover:text-ink transition-colors"
          >
            <RotateCcw className="w-4 h-4 mr-1" />
            Back to App
          </button>
          <AccountPage dark={dark} toggleDark={toggleDark} />
        </div>
      ) : !isApproved ? (
        <div className="max-w-xl mx-auto mt-16 px-6 py-12">
          <div className="flex items-center gap-3 mb-10 pb-6 border-b border-ink-200">
            <Logo variant="mark" size={28} />
            <span className="text-[10px] font-medium uppercase tracking-widest text-ink-500">Access Review</span>
          </div>
          <h1 className="text-4xl font-serif font-medium text-ink mb-6">Account Pending Approval</h1>
          <p className="text-ink-500 leading-relaxed mb-10 text-lg">
            Welcome, <span className="font-medium text-ink">{profile?.displayName}</span>.
            Your access request is currently being reviewed by our team.
            Once approved, you will have full access to the Oracle Insight engine.
          </p>

          {user && !emailVerified && (
            <div className="mb-10 p-6 bg-warning-50 border border-warning-100 rounded-md">
              <span className="text-warning-700 text-sm font-medium block mb-4">
                We sent a verification link to your email. Please verify it.
              </span>
              <button
                onClick={async () => {
                  try {
                    await sendEmailVerification(auth.currentUser!);
                    setVerificationSent(true);
                    setTimeout(() => setVerificationSent(false), 5000);
                  } catch (e) {
                    console.error("Failed to resend email", e);
                  }
                }}
                disabled={verificationSent}
                className="px-6 py-3 bg-warning-600 hover:bg-warning-700 disabled:bg-warning-700 text-paper text-[10px] font-bold uppercase tracking-widest rounded-md transition-colors"
              >
                {verificationSent ? "Sent — check your inbox" : "Resend verification email"}
              </button>
            </div>
          )}

          <div className="inline-flex items-center gap-2 px-5 py-3 bg-oxblood text-paper rounded-md">
            <p className="text-[10px] font-bold uppercase tracking-[0.3em]">
              Status: Pending Review
            </p>
          </div>
        </div>
      ) : (
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'screener' ? (
          <Screener 
            onAnalyze={(t, opts) => handleSearch(t, false, 'analyzer', opts)} 
            results={screenerResults}
            onResultsChange={(output) => {
              setScreenerResults(prev => ({
                ...prev,
                [selectedCap]: {
                  results: output.results,
                  nearMisses: output.nearMisses,
                  lastRun: new Date().toISOString()
                }
              }));
            }}
            selectedCap={selectedCap}
            onCapChange={setSelectedCap}
            onExport={handleExportScreenerPDF}
            isExporting={isExportingScreener}
            screenerRef={screenerRef}
            onLoadingChange={setScreenerLoading}
          />
        ) : activeTab === 'pre-inflection' ? (
          <div className="space-y-6">
            <div className="flex justify-center mb-6">
              <div className="bg-surface-2 p-1 rounded-md inline-flex">
                <button
                  onClick={() => setPreInflectionMode('detector')}
                  className={`px-6 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 ${
                    preInflectionMode === 'detector'
                      ? 'bg-surface text-ink border border-ink-200'
                      : 'text-ink-500 hover:text-ink'
                  }`}
                >
                  Analyze Ticker
                </button>
                <button
                  onClick={() => setPreInflectionMode('screener')}
                  className={`px-6 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 ${
                    preInflectionMode === 'screener'
                      ? 'bg-surface text-ink border border-ink-200'
                      : 'text-ink-500 hover:text-ink'
                  }`}
                >
                  Find Candidates
                </button>
              </div>
            </div>
            
            {preInflectionMode === 'detector' ? (
              <PreInflectionDetector 
                ticker={preInflectionTicker}
                onTickerChange={setPreInflectionTicker}
                onDetect={handleDetectPreInflection}
                loading={preInflectionLoading}
                result={preInflectionResult}
                error={error}
                loadingMessage={preInflectionMessage}
                currentQuote={currentQuote}
                onAnalyze={(t) => handleSearch(t)}
              />
            ) : (
              <PreInflectionScreener 
                onRunDetector={(t) => {
                  setPreInflectionTicker(t);
                  setPreInflectionMode('detector');
                  // We also need to trigger the detection
                  handleDetectPreInflection(t);
                }}
                results={preInflectionScreenResults}
                selectedCap={selectedPreInflectionCap}
                onCapChange={setSelectedPreInflectionCap}
                onResultsChange={(output) => {
                  setPreInflectionScreenResults(prev => ({
                    ...prev,
                    [selectedPreInflectionCap]: {
                      results: output.results,
                      scannedCount: output.scannedCount,
                      lastRun: new Date().toISOString()
                    }
                  }));
                }}
              />
            )}
          </div>
        ) : activeTab === 'memos' ? (
          <MemosTab 
            memos={savedMemos}
            onRefreshMemo={(ticker) => {
              handleSearch(ticker, true, 'memo-overlay');
            }}
            onCancelRefresh={handleCancelRefresh}
            marketContext={marketContext}
            isExporting={isExporting}
            onExport={handleExportPDF}
            isLoading={loading}
            loadingProgress={progress}
            loadingMessage={loadingMessage}
          />
        ) : activeTab === 'portfolio' ? (
          <Portfolio
            uid={user.uid}
            onAnalyzeTicker={(t) => {
              handleSearch(t);
            }}
          />
        ) : activeTab === 'backtest' ? (
          <BacktestTab isProduction={isProduction} />
        ) : activeTab === 'capitol' ? (
          hasCapitolEdge ? <CapitolDashboard onAnalyzeTicker={(t) => handleSearch(t)} /> : <CapitolUpgradePrompt />
        ) : activeTab === 'spinoffs' ? (
          <SpinoffIntelligence onAnalyze={(t) => { setActiveTab('analyzer'); handleSearch(t); }} />
        ) : (
          <div className="space-y-12">
            <motion.div 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8"
            >
              <div className="oi-card p-4 md:p-8 max-w-4xl mx-auto">
                <div className="flex flex-col md:flex-row items-center gap-6">
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="w-10 h-10 rounded-md bg-oxblood-50 flex items-center justify-center">
                      <ScanSearch className="w-5 h-5 text-oxblood" />
                    </div>
                    <div className="text-left">
                      <div className="text-[10px] font-medium uppercase tracking-widest text-ink-500">Oracle Insight</div>
                      <div className="text-sm font-serif font-medium">Deep Analyzer</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 flex-1 w-full">
                    <form 
                      onSubmit={(e) => { 
                        e.preventDefault();
                        setShowSuggestions(false);
                        const firstSymbol = searchSuggestions[0]?.symbol;
                        const isName = ticker.includes(' ') || /[a-z]/.test(ticker);
                        const resolved = isName && firstSymbol
                          ? firstSymbol.toUpperCase().replace(/\./g, '-')
                          : ticker.toUpperCase().replace(/\./g, '-');
                        if (resolved.trim()) handleSearch(resolved);
                      }} 
                      className="relative group flex-1"
                    >
                      <input
                        ref={searchInputRef}
                        type="text"
                        placeholder="Ticker or company name (e.g. AAPL, Apple, Costco)..."
                        value={ticker}
                        onChange={(e) => handleTickerInput(e.target.value)}
                        onFocus={() => { if (searchSuggestions.length > 0) setShowSuggestions(true); }}
                        onBlur={() => { setTimeout(() => setShowSuggestions(false), 150); }}
                        onKeyDown={(e) => {
                          if (!showSuggestions || searchSuggestions.length === 0) return;
                          if (e.key === 'ArrowDown') {
                            e.preventDefault();
                            setActiveSuggestionIndex(i => Math.min(i + 1, searchSuggestions.length - 1));
                          } else if (e.key === 'ArrowUp') {
                            e.preventDefault();
                            setActiveSuggestionIndex(i => Math.max(i - 1, -1));
                          } else if (e.key === 'Enter' && activeSuggestionIndex >= 0) {
                            e.preventDefault();
                            handleSuggestionSelect(searchSuggestions[activeSuggestionIndex].symbol);
                          } else if (e.key === 'Escape') {
                            setShowSuggestions(false);
                          }
                        }}
                        className="w-full bg-surface-2 border border-ink-200 rounded-md py-4 pl-12 pr-10 outline-none focus:ring-2 focus:ring-oxblood/15 focus:bg-surface focus:border-oxblood transition-all text-sm font-medium text-ink placeholder:text-ink-500"
                      />

                      {/* Search / loading icon — left */}
                      <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
                        {searchLoading
                          ? <Loader2 className="w-4 h-4 text-ink-500 animate-spin" />
                          : <Search className="w-4 h-4 text-ink-500 group-focus-within:text-oxblood transition-colors" />
                        }
                      </div>

                      {/* Clear button — right, only when input has text */}
                      {ticker && (
                        <button
                          type="button"
                          onClick={() => {
                            setTicker('');
                            setSearchSuggestions([]);
                            setShowSuggestions(false);
                            searchInputRef.current?.focus();
                          }}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-500 hover:text-ink transition-colors"
                          aria-label="Clear search"
                        >
                          <XCircle className="w-4 h-4" />
                        </button>
                      )}

                      {/* Typeahead dropdown */}
                      {showSuggestions && searchSuggestions.length > 0 && (
                        <div
                          ref={suggestionsRef}
                          className="absolute top-full left-0 right-0 mt-2 oi-overlay overflow-hidden z-50"
                        >
                          {searchSuggestions.map((s, idx) => (
                            <button
                              key={s.symbol}
                              type="button"
                              onMouseDown={(e) => { e.preventDefault(); handleSuggestionSelect(s.symbol); }}
                              onMouseEnter={() => setActiveSuggestionIndex(idx)}
                              className={`w-full flex items-center gap-3 px-5 py-3 text-left transition-colors ${
                                idx === activeSuggestionIndex ? 'bg-surface-2' : 'hover:bg-surface-2'
                              } ${idx !== 0 ? 'border-t border-ink-200' : ''}`}
                            >
                              <span className="inline-flex items-center justify-center bg-oxblood text-paper text-[10px] font-medium font-mono rounded-md px-2 py-1 shrink-0 min-w-[52px] tracking-wider">
                                {s.symbol}
                              </span>
                              <span className="text-sm font-medium text-ink truncate flex-1">{s.name}</span>
                              <span className="text-[10px] text-ink-500 font-medium shrink-0">{s.exchangeShortName}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </form>

                    {/* Analyze button — sits beside the input */}
                    <button
                      type="button"
                      onClick={() => {
                        setShowSuggestions(false);
                        const firstSymbol = searchSuggestions[0]?.symbol;
                        const isName = ticker.includes(' ') || /[a-z]/.test(ticker);
                        const resolved = isName && firstSymbol
                          ? firstSymbol.toUpperCase().replace(/\./g, '-')
                          : ticker.toUpperCase().replace(/\./g, '-');
                        if (resolved.trim()) handleSearch(resolved);
                      }}
                      disabled={loading || !ticker.trim()}
                      className="shrink-0 bg-oxblood text-paper text-xs font-medium px-6 py-4 rounded-md hover:bg-oxblood-800 transition-colors disabled:opacity-50 active:scale-[0.99] flex items-center gap-2 whitespace-nowrap"
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                        <>
                          <span>Analyze</span>
                          <ArrowRight className="w-3.5 h-3.5" />
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>

        {error && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className={cn(
              "mb-8 p-6 border rounded-lg flex items-center gap-4 text-left",
              error.includes("not listed") ? "bg-warning-50 border-warning-100 text-warning-700" : "bg-danger-50 border-danger-100 text-danger-600"
            )}
          >
            <AlertTriangle className={cn("w-6 h-6 shrink-0", error.includes("not listed") ? "text-warning-600" : "")} />
            <div className="text-sm font-medium">
              <p>{error}</p>
              {(error.toLowerCase().includes("api key") || error.toLowerCase().includes("forbidden") || error.includes("403")) && (
                <p className="text-[10px] uppercase tracking-widest mt-1 opacity-70">Check your API keys and network connection.</p>
              )}
              {(error.includes("403") || error.toLowerCase().includes("forbidden") || error.toLowerCase().includes("api key")) && (
                <button
                  onClick={async () => {
                    if (window.aistudio?.openSelectKey) {
                      await window.aistudio.openSelectKey();
                      setHasApiKey(true);
                      setError(null);
                    } else {
                      window.open("https://ai.google.dev/gemini-api/docs/billing", "_blank");
                    }
                  }}
                  className="mt-3 px-4 py-2 bg-danger-600 text-paper rounded-md text-[10px] font-bold uppercase tracking-widest hover:bg-danger-700 transition-colors flex items-center gap-2"
                >
                  <Settings className="w-3 h-3" />
                  Select API Key / Check Billing
                </button>
              )}
            </div>
            <button 
              onClick={() => setError(null)}
              className="ml-auto text-xs font-bold uppercase tracking-widest hover:underline"
            >
              Dismiss
            </button>
          </motion.div>
        )}

        {!hasApiKey && !error && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 p-6 bg-warning-50 border border-warning-100 rounded-lg flex items-center gap-4 text-warning-700 text-left"
          >
            <AlertCircle className="w-6 h-6 shrink-0" />
            <div className="text-sm font-medium">
              <p>No API Key Selected</p>
              <p className="text-[10px] uppercase tracking-widest mt-1 opacity-70">To use high-complexity analysis (Gemini 3.1 Pro), you must select your own API key.</p>
              <button
                onClick={async () => {
                  if (window.aistudio?.openSelectKey) {
                    await window.aistudio.openSelectKey();
                    setHasApiKey(true);
                  } else {
                    window.open("https://ai.google.dev/gemini-api/docs/billing", "_blank");
                  }
                }}
                className="mt-3 px-4 py-2 bg-warning-600 text-paper rounded-md text-[10px] font-bold uppercase tracking-widest hover:bg-warning-700 transition-colors flex items-center gap-2"
              >
                <Settings className="w-3 h-3" />
                Select API Key
              </button>
            </div>
          </motion.div>
        )}
        <AnimatePresence mode="wait">
          {!analysis && !loading && (
            <motion.div 
              key="landing"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="text-center py-12"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-oxblood-50 border border-oxblood-100 text-[10px] font-medium uppercase tracking-widest text-oxblood mb-8">
                <span className="w-1.5 h-1.5 rounded-full bg-oxblood" />
                <span>Professional Value Engine</span>
              </div>
              <h2 className="text-4xl md:text-7xl font-serif font-normal mb-8 leading-[1.1] tracking-tight text-ink">
                The <span className="italic font-normal">Intelligent</span> Investor's<br />
                Operating System.
              </h2>
              <p className="text-ink-600 max-w-2xl mx-auto mb-4 text-xl leading-relaxed">
                Identify durable competitive advantages and calculate intrinsic value 
                using Oracle Insight's core investment principles.
              </p>
              <p className="text-ink-500 max-w-xl mx-auto mb-12 text-xs font-medium uppercase tracking-widest">
                Inspired by the investment philosophy of Warren Buffett
              </p>

              {/* Buffett Indicator Snapshot */}
              <div className="max-w-4xl mx-auto mb-20">
                {marketLoading ? (
                  <div className="h-48 oi-card animate-pulse flex items-center justify-center">
                    <div className="flex items-center gap-3 text-ink-500">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span className="text-xs font-medium uppercase tracking-widest">Loading Market Context...</span>
                    </div>
                  </div>
                ) : marketError ? (
                  <div className="oi-card p-10 flex flex-col items-center justify-center gap-4">
                    <div className="w-12 h-12 rounded-md bg-[var(--color-danger-50)] flex items-center justify-center">
                      <AlertTriangle className="w-6 h-6 text-danger-600" />
                    </div>
                    <div className="text-center">
                      <h3 className="text-sm font-medium text-ink uppercase tracking-widest mb-1">Market Context Unavailable</h3>
                      <p className="text-xs text-ink-500">We're having trouble reaching the macro data server. Analysis will continue without global context.</p>
                    </div>
                  </div>
                ) : (marketContext?.buffettIndicator || marketContext?.vix) && (
                  <div className="flex flex-col gap-6">
                    {marketContext?.buffettIndicator && (
                      <motion.div 
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="oi-card overflow-hidden"
                      >
                        <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr] items-stretch">
                          <div className="bg-surface-2 p-10 flex flex-col justify-between md:border-r border-ink-200">
                            <div>
                              <div className="text-[10px] font-medium uppercase tracking-[0.3em] text-ink-500 mb-4">Macro Environment</div>
                              <h3 className="font-serif text-2xl font-medium text-ink">The Buffett Indicator</h3>
                            </div>
                            <div className="mt-8">
                              <div className={`text-5xl font-mono font-medium tracking-tight tabular-nums ${
                                marketContext.buffettIndicator.value == null ? 'text-ink'
                                : marketContext.buffettIndicator.value >= 135 ? 'text-danger-600'
                                : marketContext.buffettIndicator.value >= 115 ? 'text-warning-600'
                                : marketContext.buffettIndicator.value >= 90 ? 'text-ink'
                                : 'text-success-600'
                              }`}>
                                {marketContext.buffettIndicator.value !== null 
                                  ? `${marketContext.buffettIndicator.value.toFixed(1)}%`
                                  : "—"
                                }
                              </div>
                              <div className={`text-[10px] font-medium uppercase tracking-widest mt-2 ${
                                marketContext.buffettIndicator.value == null ? 'text-ink-500'
                                : marketContext.buffettIndicator.value >= 135 ? 'text-danger-600'
                                : marketContext.buffettIndicator.value >= 115 ? 'text-warning-600'
                                : marketContext.buffettIndicator.value >= 90 ? 'text-ink-500'
                                : 'text-success-600'
                              }`}>
                                {marketContext.buffettIndicator.status}
                              </div>
                            </div>
                          </div>
                          <div className="p-10 flex flex-col justify-center text-left">
                            <p className="text-ink-600 text-sm leading-relaxed font-serif italic mb-6">
                              Buffett's "favorite indicator" measures the total value of the stock market relative to GDP. 
                              {marketContext.buffettIndicator.description}
                            </p>
                            <div className="flex items-center gap-4">
                              <div className="flex-1 h-[1px] bg-ink-200" />
                              <div className="text-[8px] font-medium uppercase tracking-[0.3em] text-ink-500">Current Market Temperature</div>
                              <div className="flex-1 h-[1px] bg-ink-200" />
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {marketContext?.vix && (
                      <motion.div 
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="oi-card overflow-hidden"
                      >
                        <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr] items-stretch">
                          <div className="bg-surface-2 p-10 flex flex-col justify-between md:border-r border-ink-200">
                            <div>
                              <div className="text-[10px] font-medium uppercase tracking-[0.3em] text-ink-500 mb-4">Macro Environment</div>
                              <h3 className="font-serif text-2xl font-medium text-ink">Market Fear Index (VIX)</h3>
                            </div>
                            <div className="mt-8">
                              <div className={`text-5xl font-mono font-medium tracking-tight tabular-nums ${
                                marketContext.vix.value == null ? 'text-ink'
                                : marketContext.vix.value >= 30 ? 'text-danger-600'
                                : marketContext.vix.value >= 20 ? 'text-warning-600'
                                : marketContext.vix.value >= 15 ? 'text-ink'
                                : 'text-success-600'
                              }`}>
                                {marketContext.vix.value !== null 
                                  ? `${marketContext.vix.value.toFixed(2)}`
                                  : "—"
                                }
                              </div>
                              <div className={`text-[10px] font-medium uppercase tracking-widest mt-2 ${
                                marketContext.vix.value == null ? 'text-ink-500'
                                : marketContext.vix.value >= 30 ? 'text-danger-600'
                                : marketContext.vix.value >= 20 ? 'text-warning-600'
                                : marketContext.vix.value >= 15 ? 'text-ink-500'
                                : 'text-success-600'
                              }`}>
                                {marketContext.vix.sentiment}
                              </div>
                            </div>
                          </div>
                          <div className="p-10 flex flex-col justify-center text-left">
                            <p className="text-ink-600 text-sm leading-relaxed font-serif italic mb-6">
                              {marketContext.vix.description}
                            </p>
                            <div className="flex items-center gap-4">
                              <div className="flex-1 h-[1px] bg-ink-200" />
                              <div className="text-[8px] font-medium uppercase tracking-[0.3em] text-ink-500">Current Market Temperature</div>
                              <div className="flex-1 h-[1px] bg-ink-200" />
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 md:flex md:flex-wrap justify-center gap-4 md:gap-6 mb-20">
                {["AAPL", "BRK.B", "COST", "MSFT"].map((t) => (
                  <button
                    key={t}
                    onClick={() => handleSearch(t)}
                    disabled={loading}
                    className="group flex flex-col items-center p-6 md:p-8 bg-surface rounded-lg border border-ink-200 hover:border-oxblood transition-colors md:w-40 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="text-3xl font-serif font-medium text-ink mb-1 group-hover:text-oxblood transition-colors">{t}</span>
                    <span className="text-[10px] text-ink-500 uppercase tracking-widest font-medium group-hover:text-oxblood transition-colors">Analyze</span>
                  </button>
                ))}
              </div>


            </motion.div>
          )}

          {loading && (
            <AnalysisProgressOverlay 
              progress={progress} 
              loadingMessage={loadingMessage} 
              estimatedTime={estimatedTime} 
              currentQuote={currentQuote} 
            />
          )}

          {analysis && !loading && dcfModel && (
            <motion.div 
              key="analyzer-result"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-8"
            >
              {/* Company Header / Stock Ticker */}
              <div className="flex flex-col gap-8 py-10 border-b border-ink-200">
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-8">
                  <div className="flex items-start gap-6">
                    <div className="w-16 h-16 md:w-24 md:h-24 rounded-lg bg-surface-2 border border-ink-200 flex items-center justify-center text-oxblood font-serif text-2xl md:text-4xl font-medium shrink-0">
                      {analysis.ticker ? analysis.ticker[0] : '?'}
                    </div>
                    <div className="space-y-2">
                      <div className="flex flex-wrap items-center gap-2 md:gap-3">
                        <span className="px-3 py-1 rounded-sm bg-oxblood text-paper text-[10px] font-medium uppercase tracking-[0.2em] font-mono">{analysis.ticker}</span>
                        <div className="h-4 w-[1px] bg-ink-200" />
                        <span className="text-[10px] font-medium uppercase tracking-[0.2em] text-ink-500">Oracle Evaluation Report</span>
                        <Tooltip content={`Institutional data status: ${analysis.isProduction ? 'Connected' : 'Restricted'}. ${!analysis.isProduction ? 'The system is using AI-inferred data because the financial APIs (FMP/Alpha Vantage) are restricted or returned empty data. Accuracy may be lower.' : 'Using institutional-grade real-time data.'}`}>
                          <div className={cn(
                            "flex items-center gap-1.5 px-3 py-1 rounded-sm text-[8px] font-medium uppercase tracking-widest cursor-help transition-all",
                            analysis.isProduction ? "bg-[var(--color-success-50)] border border-[var(--color-success-100)] text-[var(--color-success-700)]" : "bg-[var(--color-warning-50)] border border-[var(--color-warning-100)] text-[var(--color-warning-700)] animate-pulse"
                          )}>
                            {analysis.isProduction ? <ShieldCheck className="w-2.5 h-2.5" /> : <AlertCircle className="w-2.5 h-2.5" />}
                            <span>{analysis.isProduction ? 'Institutional Grade' : 'AI-Inferred Data (Restricted)'}</span>
                          </div>
                        </Tooltip>
                        {analysis.priceType && (
                          <>
                            <div className="h-4 w-[1px] bg-ink-200" />
                            <span className="text-[10px] font-medium uppercase tracking-[0.2em] text-ink-500">{analysis.priceType}</span>
                          </>
                        )}
                        {analysis.archetype && (
                          <>
                            <div className="h-4 w-[1px] bg-ink-200" />
                            <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-sm bg-surface-2 border border-ink-200 text-ink text-[8px] font-medium uppercase tracking-widest">
                              {analysis.archetype.category === "YIELD UTILITY / BOND PROXY" && <Shield className="w-2.5 h-2.5" />}
                              {analysis.archetype.category === "YIELD + CONSOLIDATOR" && <Layers className="w-2.5 h-2.5" />}
                              {analysis.archetype.category === "TURNAROUND / RE-INFLECTION CANDIDATE" && <RotateCcw className="w-2.5 h-2.5" />}
                              {analysis.archetype.category === "COMPOUNDER" && <TrendingUp className="w-2.5 h-2.5" />}
                              <span>{analysis.archetype.category}</span>
                            </div>
                          </>
                        )}
                        {analysis.isObviouslyAttractive && (
                          <>
                            <div className="h-4 w-[1px] bg-ink-200" />
                            <Tooltip content="Buffett Filter: This business feels obviously attractive even without a calculator.">
                              <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-sm bg-success-600 text-paper text-[8px] font-medium uppercase tracking-widest cursor-help">
                                <CheckCircle2 className="w-2.5 h-2.5" />
                                <span>Obviously Attractive</span>
                              </div>
                            </Tooltip>
                          </>
                        )}
                        {analysis.isGrowthStructurallyInevitable && (
                          <>
                            <div className="h-4 w-[1px] bg-ink-200" />
                            <Tooltip content="Munger's Edge: Growth is structurally embedded in the business model (e.g., Costco, Apple).">
                              <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-sm bg-success-600 text-paper text-[8px] font-medium uppercase tracking-widest cursor-help">
                                <TrendingUp className="w-2.5 h-2.5" />
                                <span>Inevitable Growth</span>
                              </div>
                            </Tooltip>
                          </>
                        )}
                        {analysis.shouldActDecisively && (
                          <>
                            <div className="h-4 w-[1px] bg-ink-200" />
                            <Tooltip content="Oracle's Directive: The opportunity is 'good enough' to act decisively now. Don't let the model veto a great business.">
                              <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-sm bg-oxblood text-paper text-[8px] font-medium uppercase tracking-widest cursor-help">
                                <Zap className="w-2.5 h-2.5" />
                                <span>Act Decisively</span>
                              </div>
                            </Tooltip>
                          </>
                        )}
                        {analysis.isInevitableCompounderOverride && (
                          <>
                            <div className="h-4 w-[1px] bg-ink-200" />
                            <Tooltip content="Inevitable Compounder Rule: Elite business with high predictability. Minimum position (2-3%) always allowed to avoid missing out on long-term compounding.">
                              <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-sm bg-oxblood text-paper text-[8px] font-medium uppercase tracking-widest cursor-help">
                                <History className="w-2.5 h-2.5" />
                                <span>Inevitable Compounder</span>
                              </div>
                            </Tooltip>
                          </>
                        )}
                        {analysis.entityType === 'Type 5' && (
                          <>
                            <div className="h-4 w-[1px] bg-ink-200" />
                            <Tooltip content={
                              analysis.isType5DataContaminated
                                ? "Recent Spin-off: This company was recently separated from a parent company. Available financial data reflects the parent's consolidated historical operations, not this entity's standalone performance. Oracle cannot produce a reliable intrinsic value until 2-3 years of audited standalone filings are available. The PASS verdict is deliberate — not a system error."
                                : "Limited Financial History: This company has fewer than 3 years of standalone public financial data. Oracle has issued an analysis with wider confidence bands, but results carry additional uncertainty compared to established companies."
                            }>
                              <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-sm text-[8px] font-medium uppercase tracking-widest cursor-help border ${
                                analysis.isType5DataContaminated
                                  ? 'bg-[var(--color-danger-50)] text-[var(--color-danger-700)] border-[var(--color-danger-100)]'
                                  : 'bg-[var(--color-warning-50)] text-[var(--color-warning-700)] border-[var(--color-warning-100)]'
                              }`}>
                                <AlertTriangle className="w-2.5 h-2.5" />
                                <span>{analysis.isType5DataContaminated ? 'Recent Spin-off' : 'Limited History'}</span>
                              </div>
                            </Tooltip>
                          </>
                        )}
                      </div>
                      <h2 className="text-3xl sm:text-5xl md:text-7xl font-serif font-medium tracking-tight text-ink leading-none">
                        {analysis.name}
                      </h2>
                      {analysis.dataTimestamp && (
                        <div className="text-[10px] font-medium text-ink-500 uppercase tracking-widest pt-1">As of {analysis.dataTimestamp}</div>
                      )}
                    </div>
                  </div>
                  
                    <div className="flex flex-col items-start md:items-end gap-1">
                      <div className="flex items-baseline gap-3">
                        <div className="text-4xl sm:text-6xl md:text-7xl font-mono font-medium tracking-tight tabular-nums text-ink">
                          {formatNumber(analysis.currentPrice, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                        <div className="text-xl font-medium text-ink-500 uppercase tracking-widest">USD</div>
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-3 md:justify-end">
                        {(analysis.priceChange !== undefined && analysis.priceChangePercent !== undefined) && (
                          <div className={cn(
                            "flex items-center gap-2 text-xl font-medium tabular-nums",
                            analysis.priceChange >= 0 ? "text-success-600" : "text-danger-600"
                          )}>
                            <span>{analysis.priceChange >= 0 ? '+' : ''}{safeToFixed(analysis.priceChange, 2)}</span>
                            <span>({analysis.priceChangePercent >= 0 ? '+' : ''}{safeToFixed(analysis.priceChangePercent, 2)}%)</span>
                          </div>
                        )}

                        {analysis.aftermarketPrice && analysis.aftermarketPrice > 0 && (
                          <div className="flex items-center gap-2 px-3 py-1 bg-surface-2 border border-ink-200 rounded-sm">
                            <div className="w-1.5 h-1.5 rounded-full bg-ink-500 animate-pulse" />
                            <span className="text-[10px] font-medium uppercase tracking-widest text-ink-600 tabular-nums">After-Hours: ${safeToFixed(analysis.aftermarketPrice, 2)}</span>
                            {analysis.aftermarketChange !== undefined && (
                              <span className={cn(
                                "text-[10px] font-medium tabular-nums",
                                analysis.aftermarketChange >= 0 ? "text-success-600" : "text-danger-600"
                              )}>
                                {analysis.aftermarketChange >= 0 ? '+' : ''}{safeToFixed(analysis.aftermarketChange, 2)}
                              </span>
                            )}
                          </div>
                        )}
                        
                        <div className={cn(
                          "px-3 py-1 rounded-sm text-[10px] font-medium uppercase tracking-widest border tabular-nums",
                          dcfModel.valuationStatus.statusColor === "green" && "bg-[var(--color-success-50)] text-[var(--color-success-700)] border-[var(--color-success-100)]",
                          dcfModel.valuationStatus.statusColor === "red" && "bg-[var(--color-danger-50)] text-[var(--color-danger-700)] border-[var(--color-danger-100)]",
                          dcfModel.valuationStatus.statusColor === "amber" && "bg-[var(--color-warning-50)] text-[var(--color-warning-700)] border-[var(--color-warning-100)]"
                        )}>
                          {dcfModel.valuationStatus.label} ({safeToFixed(Math.abs(dcfModel.valuationStatus.diffPercent), 1)}%)
                        </div>
                        
                        <div className="px-3 py-1 rounded-sm text-[10px] font-medium uppercase tracking-widest border border-ink-200 bg-surface-2 text-ink-600 tabular-nums">
                          Intrinsic: {dcfModel.isEstimable ? `$${safeToFixed(dcfModel.intrinsicValuePerShare, 2)}` : "NOT ESTIMABLE"}
                        </div>
                      </div>
                    </div>
                </div>
              </div>

                {showRefreshBanner && (
                  <div className="bg-[var(--color-warning-50)] border border-[var(--color-warning-100)] rounded-md p-3 mt-8 mb-2 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-[var(--color-warning-700)] mx-2">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 shrink-0" />
                      <span className="text-[10px] sm:text-xs font-mono uppercase tracking-wider font-medium text-[var(--color-warning-700)]">
                        Last run {currentMemoAge === 0 ? 'today' : `${currentMemoAge} day${(currentMemoAge || 0) === 1 ? '' : 's'} ago`} · Data may have changed
                      </span>
                    </div>
                    <button
                      onClick={() => handleSearch(ticker, true)}
                      className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-1.5 bg-surface border border-ink-200 hover:border-oxblood text-ink-600 hover:text-oxblood text-[10px] sm:text-xs font-medium uppercase tracking-wider rounded-md transition-colors"
                    >
                      <RefreshCw className="w-3.5 h-3.5 shrink-0" />
                      Refresh Analysis
                    </button>
                  </div>
                )}

                {/* Analysis Tabs */}
                <div className="sticky top-[5rem] z-40 bg-paper/95 backdrop-blur-md py-3 md:py-4 mb-8 border-b border-ink-200">
                  <div className="flex flex-wrap gap-1.5 md:gap-2 px-2 pb-1">
                    {[
                      { id: 'overview', label: 'Overview', shortLabel: 'Overview', icon: LayoutDashboard },
                      { id: 'valuation', label: 'Valuation', shortLabel: 'Value', icon: Calculator },
                      { id: 'business', label: 'Business Quality', shortLabel: 'Business', icon: ShieldCheck },
                      { id: 'risk', label: 'Risk & Signals', shortLabel: 'Risk', icon: AlertTriangle },
                      { id: 'memo', label: 'Investment Memo', shortLabel: 'Memo', icon: FileText },
                    ].map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveAnalysisTab(tab.id)}
                        className={cn(
                          "flex items-center gap-1.5 px-3 py-2 md:px-6 md:py-2.5 rounded-md text-[9px] md:text-[10px] font-medium uppercase tracking-widest transition-colors border",
                          activeAnalysisTab === tab.id
                            ? "bg-oxblood text-paper border-oxblood"
                            : "bg-surface text-ink-600 hover:text-ink border-ink-200 hover:border-oxblood"
                        )}
                      >
                        <tab.icon className="w-3 h-3 md:w-3.5 md:h-3.5" />
                        <span className="hidden sm:inline">{tab.label}</span>
                        <span className="sm:hidden">{tab.shortLabel}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Overview Tab Content */}
                {activeAnalysisTab === 'overview' && (
                  <OverviewTab
                    analysis={analysis}
                    marketContext={marketContext}
                    dcfModel={dcfModel}
                    historicalData={historicalData}
                    chartRange={chartRange}
                    chartTab={chartTab}
                    comparisonData={comparisonData}
                    chartLoading={chartLoading}
                    onChartRangeChange={setChartRange}
                    onChartTabChange={setChartTab}
                    useForwardMetrics={useForwardMetrics}
                    onToggleForwardMetrics={setUseForwardMetrics}
                  />
                )}
              {activeAnalysisTab === 'business' && <BusinessTab analysis={analysis} dcfModel={dcfModel} />}
              {activeAnalysisTab === 'risk' && <RiskTab analysis={analysis} />}
              {activeAnalysisTab === 'valuation' && (
                <ValuationTab
                  analysis={analysis}
                  dcfModel={dcfModel!}
                  marketContext={marketContext}
                  assumptions={assumptions}
                  onAssumptionChange={(key, value) => setAssumptions(prev => ({ ...prev, [key]: value }))}
                  onResetAssumptions={resetAssumptions}
                  useForwardMetrics={useForwardMetrics}
                  isForward={useForwardMetrics}
                />
              )}
              {activeAnalysisTab === 'memo' && (
              <MemoTab
                analysis={analysis}
                dcfModel={dcfModel!}
                marketContext={marketContext}
                memoRef={memoRef}
                isExporting={isExporting}
                onExport={handleExportPDF}
              />
            )}

              {/* Production Guidance (Full Width at Bottom) */}
              {!isProduction && (
                <section className="bg-black text-white p-12 rounded-[3rem] shadow-2xl shadow-black/20 overflow-hidden relative group mt-8">
                  <div className="absolute -right-10 -bottom-10 opacity-10 group-hover:scale-110 transition-transform">
                    <HelpCircle className="w-64 h-64" />
                  </div>
                  <div className="relative z-10">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-8">
                      <div className="max-w-xl">
                        <div className="flex items-center gap-3 mb-6">
                          <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center">
                            <HelpCircle className="w-6 h-6 text-[#FFD700]" />
                          </div>
                          <h3 className="font-serif text-3xl font-bold italic text-white">Production Path</h3>
                        </div>
                        <p className="text-lg text-gray-300 leading-relaxed mb-8">
                          This model uses AI-inferred data. For professional deployment, integrate these institutional-grade APIs for real-time accuracy and compliance.
                        </p>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 w-full md:w-auto">
                        {[
                          { name: "Financial Modeling Prep", use: "10-K, Profiles, Quotes" },
                          { name: "Alpha Vantage", use: "Real-time FCF & Ratios" },
                          { name: "Polygon.io", use: "Market Data & News" }
                        ].map((api, i) => (
                          <div key={i} className="flex flex-col gap-3 p-6 rounded-3xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors group/item">
                            <ArrowRight className="w-5 h-5 text-[#FFD700] group-hover/item:translate-x-1 transition-transform" />
                            <div className="space-y-1">
                              <div className="text-xs font-bold uppercase tracking-widest">{api.name}</div>
                              <div className="text-[10px] text-gray-500 italic">{api.use}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </section>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    )}
  </main>
)}

        <footer className="bg-surface-2 text-ink-600 py-16 px-4 md:px-8 border-t border-ink-200 mt-20">
          <div className="max-w-6xl mx-auto flex flex-col gap-8">
            <Logo size={30} variant="lockup" />

            <div className="text-[11px] leading-relaxed space-y-4 max-w-5xl text-ink-600">
              <p>
                <strong className="text-ink font-medium">Not Investment Advice / Not a Registered Investment Adviser:</strong> Oracle Insight is an informational and educational tool only. Nothing on this platform, including any analysis, verdict, score, memo, signal, or other output, constitutes investment advice, financial advice, trading advice, or any recommendation to buy, sell, or hold any security. No content produced by Oracle Insight should be construed as a solicitation or offer to buy or sell any financial instrument. Oracle Insight is not registered as an investment adviser with the U.S. Securities and Exchange Commission (SEC) or any state securities authority. Use of this platform does not create an adviser-client relationship of any kind. Oracle Insight does not manage assets, execute trades, or provide personalized investment recommendations.
              </p>
              <p>
                <strong className="text-ink font-medium">Consult a Qualified Professional / AI-Generated Content:</strong> Before making any investment decision, users are strongly encouraged to consult with a qualified financial advisor, certified financial planner (CFP), or licensed investment professional who can evaluate their individual financial situation, goals, risk tolerance, and tax circumstances. Oracle Insight is not a substitute for personalized professional advice. All analysis, memos, verdicts, and signals produced by Oracle Insight are generated by artificial intelligence using publicly available data. AI-generated content may contain errors, omissions, or outdated information. Outputs should not be relied upon as the sole basis for any investment decision.
              </p>
              <p>
                <strong className="text-ink font-medium">Past Performance & Data Accuracy:</strong> Any references to historical data, past market conditions, or prior company performance are provided for informational context only. Past performance is not indicative of future results. No representation is made that any analysis will result in profits or avoid losses. Financial data displayed on this platform is sourced from third-party data providers. Oracle Insight makes no warranty, express or implied, regarding the accuracy, completeness, timeliness, or fitness for any purpose of such data. Users should independently verify any information before relying on it.
              </p>
              <p>
                <strong className="text-ink font-medium">No Liability & Forward-Looking Statements:</strong> To the fullest extent permitted by law, Oracle Insight and its operators shall not be liable for any investment losses, financial damages, or consequential damages of any kind arising from use of this platform or reliance on its outputs. Certain analysis outputs may include projections, estimates, or forward-looking statements. These are inherently uncertain and subject to change. Actual results may differ materially from any projections or estimates contained in Oracle Insight outputs.
              </p>
            </div>

            <div className="pt-8 border-t border-ink-200 flex flex-col md:flex-row justify-between items-center gap-4 text-[11px] text-ink-500">
              <p>&copy; {new Date().getFullYear()} Oracle Insight. All rights reserved.</p>
              <div className="flex gap-8">
                <button onClick={() => setLegalView('terms')} className="hover:text-oxblood transition-colors">Terms of Service</button>
                <button onClick={() => setLegalView('privacy')} className="hover:text-oxblood transition-colors">Privacy Policy</button>
                <button className="hover:text-oxblood transition-colors">Contact</button>
              </div>
            </div>
          </div>
        </footer>

      <LegalModal view={legalView} onClose={() => setLegalView(null)} />

      {/* Mobile Bottom Navigation — hidden on md and above */}
      <nav className="fixed bottom-0 left-0 right-0 z-[70] md:hidden bg-paper border-t border-ink-200"
           style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <div className="flex items-center justify-around px-2 py-3">

          {/* Analyzer */}
          <button
            onClick={() => setActiveTab('analyzer')}
            className={cn(
              "flex flex-col items-center gap-1 px-2 py-1 rounded-xl transition-all",
              activeTab === 'analyzer' ? "text-oxblood" : "text-ink-500"
            )}
          >
            <ScanSearch className={cn("w-5 h-5", activeTab === 'analyzer' && "stroke-[2.5]")} />
            <span className="text-[9px] font-bold uppercase tracking-widest">Analyze</span>
          </button>

          {/* Funnel */}
          <button
            onClick={() => setActiveTab('screener')}
            className={cn(
              "flex flex-col items-center gap-1 px-2 py-1 rounded-xl transition-all",
              activeTab === 'screener' ? "text-oxblood" : "text-ink-500"
            )}
          >
            <Filter className={cn("w-5 h-5", activeTab === 'screener' && "stroke-[2.5]")} />
            <span className="text-[9px] font-bold uppercase tracking-widest">Funnel</span>
          </button>

          {/* Pre-Inflection */}
          <button
            onClick={() => setActiveTab('pre-inflection')}
            className={cn(
              "flex flex-col items-center gap-1 px-2 py-1 rounded-xl transition-all",
              activeTab === 'pre-inflection' ? "text-oxblood" : "text-ink-500"
            )}
          >
            <TrendingUp className={cn("w-5 h-5", activeTab === 'pre-inflection' && "stroke-[2.5]")} />
            <span className="text-[9px] font-bold uppercase tracking-widest">Signal</span>
          </button>

          {/* Portfolio */}
          <button
            onClick={() => setActiveTab('portfolio')}
            className={cn(
              "flex flex-col items-center gap-1 px-2 py-1 rounded-xl transition-all",
              activeTab === 'portfolio' ? "text-oxblood" : "text-ink-500"
            )}
          >
            <Briefcase className={cn("w-5 h-5", activeTab === 'portfolio' && "stroke-[2.5]")} />
            <span className="text-[9px] font-bold uppercase tracking-widest">Portfolio</span>
          </button>

          {/* Memos */}
          <button
            onClick={() => setActiveTab('memos')}
            className={cn(
              "flex flex-col items-center gap-1 px-2 py-1 rounded-xl transition-all relative",
              activeTab === 'memos' ? "text-oxblood" : "text-ink-500"
            )}
          >
            <FileText className={cn("w-5 h-5", activeTab === 'memos' && "stroke-[2.5]")} />
            <span className="text-[9px] font-bold uppercase tracking-widest">Memos</span>
            {savedMemos.length > 0 && (
              <span className="absolute top-0.5 right-1.5 bg-oxblood text-paper text-[8px] font-medium px-1.5 py-0.5 rounded-sm min-w-[16px] text-center">
                {savedMemos.length}
              </span>
            )}
          </button>

          {/* Backtest */}
          <button
            onClick={() => setActiveTab('backtest')}
            className={cn(
              "flex flex-col items-center gap-1 px-2 py-1 rounded-xl transition-all",
              activeTab === 'backtest' ? "text-oxblood" : "text-ink-500"
            )}
          >
            <BarChart2 className={cn("w-5 h-5", activeTab === 'backtest' && "stroke-[2.5]")} />
            <span className="text-[9px] font-bold uppercase tracking-widest">Backtest</span>
          </button>
          
          {/* Capitol */}
          {hasCapitolEdge && (
            <button
              onClick={() => setActiveTab('capitol')}
              className={cn(
                "flex flex-col items-center gap-1 px-2 py-1 rounded-xl transition-all",
                activeTab === 'capitol' ? "text-oxblood" : "text-ink-500"
              )}
            >
              <Landmark className={cn("w-5 h-5", activeTab === 'capitol' && "stroke-[2.5]")} />
              <span className="text-[9px] font-bold uppercase tracking-widest">Capitol</span>
            </button>
          )}
          {/* TODO: Replace hide with upgrade prompt when billing live */}

          {/* More / Profile */}
          <button
            onClick={() => setShowProfileMenu(prev => !prev)}
            className={cn(
              "flex flex-col items-center gap-1 px-2 py-1 rounded-xl transition-all",
              showProfileMenu ? "text-oxblood" : "text-ink-500"
            )}
          >
            <UserCircle className={cn("w-5 h-5", showProfileMenu && "stroke-[2.5]")} />
            <span className="text-[9px] font-bold uppercase tracking-widest">More</span>
          </button>

        </div>
      </nav>

      {/* Profile menu overlay — appears above bottom nav when More is tapped */}
      {showProfileMenu && (
        <>
          {/* Tap-outside dismissal backdrop */}
          <div
            className="fixed inset-0 z-[75] md:hidden"
            onClick={() => setShowProfileMenu(false)}
          />

          {/* Menu card */}
          <div className="fixed bottom-20 right-3 z-[80] md:hidden oi-overlay overflow-hidden min-w-[180px]"
               style={{ marginBottom: 'env(safe-area-inset-bottom)' }}>

            {isAdmin && (
              <button
                onClick={() => {
                  setShowAdmin(prev => !prev);
                  setShowAccount(false);
                  setShowProfileMenu(false);
                }}
                className="flex items-center gap-3 w-full px-5 py-4 hover:bg-surface-2 transition-colors border-b border-ink-200"
              >
                <LayoutDashboard className="w-4 h-4 text-ink-500" />
                <span className="text-sm font-semibold text-ink">Admin</span>
              </button>
            )}

            <button
              onClick={() => {
                setShowAccount(true);
                setShowAdmin(false);
                setShowProfileMenu(false);
              }}
              className="flex items-center gap-3 w-full px-5 py-4 hover:bg-surface-2 transition-colors border-b border-ink-200 text-ink"
            >
              <Settings className="w-4 h-4 text-ink-500" />
              <span className="text-sm font-semibold text-ink">Account</span>
            </button>

            <button
              onClick={() => {
                logout();
                setShowProfileMenu(false);
              }}
              className="flex items-center gap-3 w-full px-5 py-4 hover:bg-surface-2 transition-colors text-ink-600"
            >
              <LogOut className="w-4 h-4" />
              <span className="text-sm font-semibold">Sign Out</span>
            </button>

          </div>
        </>
      )}

      {showWarning && (
        <SessionWarningModal
          secondsRemaining={secondsLeft}
          onStaySignedIn={handleStaySignedIn}
          onSignOut={handleTimeout}
        />
      )}
      </div>
    </TooltipProvider>
  );
}



export default function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

function AppContent() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-paper flex items-start justify-start p-10 md:p-16">
        <div className="flex flex-col gap-6 max-w-xs">
          <div className="text-oxblood animate-pulse">
            <Logo variant="mark" size={40} />
          </div>
          <div className="h-px w-full bg-ink-200" />
          <p className="text-[10px] font-medium uppercase tracking-widest text-ink-500">Initializing The Oracle</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <LandingPage />;
  }

  return (
    <TooltipProvider>
      <MainApp />
    </TooltipProvider>
  );
}
